import { useEffect, useState, useMemo, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, idr } from "../lib/api";
import { hargaSaatPilihUnit, ringkasInfoUnit, terimaPilihanJual } from "../lib/hargaUnit";
import {
  Plus, Trash2, X, CheckCircle2, XCircle, Pencil, Send, Clock, User, ShoppingCart,
  AlertTriangle, FileText, Eye, CreditCard, Repeat, Phone, MapPin, StickyNote,
  Sparkles, Lock, Ticket,
} from "lucide-react";
import BarcodeScanner from "../components/hris/BarcodeScanner";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { bolehPakaiAI } from "../lib/izinAI";
import AIProductChat from "../components/hris/AIProductChat";
import InputRupiah from "../components/InputRupiah";
import { useCabangOptions } from "../lib/cabang";
import { DatePicker } from "@/components/ui/date-picker";
import { hariIniISO } from "@/lib/tanggal";
import {
  MetodePembayaranManager, TombolKelolaMetode, bolehKelolaMetode,
} from "../components/pembayaran/MetodePembayaran";
import PetunjukKreditRetur from "../components/pembayaran/PetunjukKreditRetur";
import useKreditRetur from "../hooks/useKreditRetur";
import {
  rincianBayar, hitungPpn, hitungBiayaAdmin, bulatkanSepertiPython, dppNota,
} from "../lib/rincianBayar";
import {
  diskonBaris, diskonBarisTotal, ambilDiskonLama, diskonEfektifForm, adminEfektifForm,
  galatCampurDiskon, galatDiskonBaris, itemsFormDariDokumen, itemsKePayload, bagianDiskonPayload,
  barisBaru, barisPembayaranDariDokumen, uangMukaBentukLama, ikutkanSisa, aturBarisUtamaTempo,
  galatBarisPembayaran, bagianBayarPayload, nilaiTotalKasir, adalahKreditReturMetode,
} from "../lib/payloadNota";
import BlokPembayaran from "../components/pembayaran/BlokPembayaran";
import KonfirmasiTotalPembayaran from "../components/pembayaran/KonfirmasiTotalPembayaran";
import DaftarBarisPembayaran from "../components/pembayaran/DaftarBarisPembayaran";
import {
  TabelDesktop, DaftarKartuMobile, KartuBaris, KartuBarisHeader, KartuBarisMeta, KartuBarisAksi,
} from "@/components/ui/responsive-table";
import SourceInfoField, { useSourceInfoConfig, validasiSourceInfo } from "../components/SourceInfoField";
import PilihPelangganNota from "../components/pelanggan/PilihPelangganNota";
import useAturanIdentitasNota from "../hooks/useAturanIdentitasNota";
import {
  periksaBentukHp, kunciNama, butuhKartu, hpBerubah as hpBerubahLib, hpTampilan,
  cabangKanonikDariOpsi,
} from "../lib/identitasNota";
import { useModalGuard, useModalLayer, UnsavedGuardBar } from "../hooks/useModalGuard";
import { useSortState, sortRows, SortTh } from "@/components/ui/sort";
import {
  KATEGORI_ITEM, KATEGORI_DEFAULT, labelKategori, normalKategori, deriveKode, deriveKategoriTeksBebas,
} from "@/lib/kategoriBarang";
import BundleKomponenPicker, {
  useResepBundlingAktif, useProdukInfoCache, bangunKomponenKosong,
  cariAlasanBundleBelumSiap, kumpulkanSemuaImeiForm,
} from "@/components/bundling/BundleKomponenPicker";

const emptyForm = () => ({
  id: null,
  tanggal: hariIniISO(),
  waktu: new Date().toTimeString().slice(0, 5),
  nama_pelanggan: "", no_hp: "",
  items: [{ nama_barang: "", harga: 0, kode: "HP", kategori: KATEGORI_DEFAULT, sku: "", imei: "", is_bonus: false, komponen: null, diskon_rp: 0 }],
  imei: "", metode_pembayaran: "Cash",
  is_split: false,
  // Baris pembayaran (batch N3, brief 22 Sep D4): baris UTAMA mengikuti sisa
  // tagihan sampai kasir mengubahnya; baris tambahan wajib keterangan. Lihat
  // lib/payloadNota.js bagian "BARIS PEMBAYARAN".
  payments: [barisBaru("Cash", 0, true)],
  trade_in: false, trade_in_note: "",
  trade_in_items: [{ nama: "", status: "", warna: "", bh: "", kelengkapan: "",
                     imei: "", catatan: "", harga: 0 }],
  // "Harga Terima" sudah tidak ada di form (D6) — diganti Total Pembayaran
  // yang diisi KASIR (kolom di atas Catatan / jendela approve).
  harga_terima: 0, catatan: "", nama_penjual: "", branch: "",
  total_pembayaran_kasir: "",
  // Uang muka draf LAMA bertempo yang dibuka layar ini (null = bukan draf
  // seperti itu) — perubahannya ditanyakan dulu sebelum simpan (K27).
  _uang_muka_lama: null,
  // Jenis pelanggan & tempo (owner 29 Agu 2026). Bawaan "umum" tanpa tempo =
  // perilaku lama persis.
  jenis_pelanggan: "umum", tempo_pembayaran: "", pic_approval_piutang: "",
  // Diskon kini PER BARANG (`items[].diskon_rp`, brief 22 Sep 2026 D2+D3);
  // PIC Approval Diskon satu per nota. Kolom diskon nota & biaya admin sudah
  // tidak ada di form — `_diskon_lama` memegang nilai LAMA draf yang dibuka
  // (lihat lib/payloadNota.js `ambilDiskonLama`), tampil baca-saja.
  diskon_pic_approval: "",
  _diskon_lama: null,
  // Rekening tujuan pengembalian dana saat DOWNGRADE (brief poin 6-7).
  refund_nama_bank: "", refund_no_rekening: "", refund_atas_nama: "",
  dp_id: "", dp_nominal: "",
  // PPN (keputusan owner 18 Agu 2026) — toggle tingkat DOKUMEN. Tarif &
  // nominal sesungguhnya DIHITUNG SERVER saat approve (lihat
  // nota_module.py::_build_sales_doc_from_nota) — angka di sini hanya
  // pratinjau untuk kasir, bukan sumber kebenaran.
  kena_ppn: false,
  // Sumber Info / atribusi marketing (brief bos 11 Agu 2026, WAJIB selama
  // saklar darurat aktif — lihat components/SourceInfoField.jsx). "" (bukan
  // null) supaya <select> terkontrol dari awal render.
  source_info: "", source_ref_id: "",
  // Kode tiket antrian (brief 15 Sep G5/G6). Kosong = nota tanpa tiket,
  // perilaku lama persis; dikirim sebagai null.
  kode_antrian: "",
  // N1 (brief 22 Sep): kartu pelanggan yang DIPILIH (chip) dan potret
  // identitas saat nota dibuka untuk diedit (null = nota baru). Keduanya
  // hanya milik layar — dibuang dari payload di submit().
  _kartu: null,
  _identitas_awal: null,
});

/** Status wajib-scan milik pengguna ini, atau null kalau tidak bisa diambil.
 *
 *  403 dan galat jaringan APA PUN sengaja dianggap "tidak wajib" — cermin
 *  fail-open backend. Kalau layar fail-closed sementara server fail-open,
 *  kasir terkunci dari Input Nota gara-gara sinyal jelek padahal server
 *  sendiri akan menerima notanya.
 */
async function ambilTiketSaya() {
  try {
    const { data } = await api.get("/antrian/tiket-saya");
    return data || null;
  } catch {
    return null;
  }
}

/** Tiket yang masuk akal untuk nota ini: yang sedang dipegang dulu, lalu
 *  tiket "membeli" hari ini (satu tiket boleh untuk beberapa nota). */
function daftarTiketPilihan(st) {
  const hasil = [];
  const sudah = new Set();
  if (st?.tiket?.kode_antrian) {
    hasil.push({ ...st.tiket, _keterangan: "sedang Anda layani" });
    sudah.add(st.tiket.kode_antrian);
  }
  (st?.tiket_membeli_hari_ini || []).forEach((t) => {
    if (!t?.kode_antrian || sudah.has(t.kode_antrian)) return;
    sudah.add(t.kode_antrian);
    hasil.push({ ...t, _keterangan: `membeli hari ini · ${Number(t.jumlah_nota) || 0} nota` });
  });
  return hasil;
}

/** Nomor antrian untuk kode yang tertaut di form — kode datang dari URL tanpa
 *  nomornya, jadi dicari di status tiket yang sudah termuat. */
function nomorUntukKode(st, kode) {
  if (!kode) return null;
  const t = daftarTiketPilihan(st).find((x) => x.kode_antrian === kode);
  return t?.nomor_antrian || null;
}

// Pratinjau SAJA (server yang menentukan angka final & tarif yang tersimpan —
// lihat komentar TARIF_PPN_DEFAULT di backend). Dipakai supaya kasir sudah
// melihat perkiraan Total sebelum submit, tanpa menunggu round-trip API.
const PPN_PERSEN_PREVIEW = 11;

const STATUS_META = {
  pending:  { label: "Menunggu Approval", color: "amber",   icon: Clock },
  approved: { label: "Disetujui",         color: "emerald", icon: CheckCircle2 },
  rejected: { label: "Ditolak",           color: "rose",    icon: XCircle },
};

// Pilihan sub-kolom unit trade-in (brief owner 29 Agu poin 4). Daftar
// TERTUTUP, bukan ketikan bebas: nama unit second dirakit dari kolom-kolom ini
// dan dipakai untuk menentukan harga jualnya kembali — "RESMI" vs "resmi" vs
// "Garansi Resmi" untuk barang yang sama membuat katalog second tidak bisa
// dikelompokkan (pelajaran 85 unit second tanpa kondisi, brief 23 Agu).
const TI_STATUS = ["RESMI", "BEACUKAI", "UNLOCK", "SIMLOCK", "INTER", "SECOND MINUS"];
const TI_KELENGKAPAN = ["FULLSET ORI", "MINUS CAS 1 SET", "MINUS KEPALA CAS",
                        "MINUS KABEL", "UNIT ONLY"];

/** Rakit nama unit trade-in jadi SATU kesatuan (brief poin 5).
 *
 *  Contoh yang diminta owner:
 *      IPHONE 13 128GB / RESMI / STARLIGHT / 85% / FULLSET ORI
 *
 *  Bagian yang kosong DILEWATI, tidak diganti tanda hubung — "IPHONE 13 / / /"
 *  lebih sulit dibaca daripada nama yang lebih pendek. IMEI/SN sengaja TIDAK
 *  ikut: brief memintanya di kolom berikutnya.
 */
function rakitNamaTradeIn(t) {
  return [t.nama, t.status, t.warna, t.bh, t.kelengkapan]
    .map(x => (x || "").trim())
    .filter(Boolean)
    .join(" / ");
}


export default function InputNota() {
  const { user, can } = useAuth();
  const BRANCHES = useCabangOptions({ denganKosong: true });
  const canApprove = ["superadmin", "admin", "kasir_pos"].includes(user?.role);
  // Cermin `payment_methods_module._boleh_kelola` di backend (role lama +
  // izin eksplisit). Daftar role-nya TIDAK lagi ditulis ulang di sini —
  // lihat `components/pembayaran/MetodePembayaran.jsx`.
  const canManageMethods = bolehKelolaMetode(user);
  // Autocomplete produk memakai modul inventory_produk — modul berbeda dari
  // nota_sales. Kalau tidak diizinkan, kolom nama barang tetap ketik bebas
  // (tidak pernah memanggil endpoint lookup, jadi tidak ada 403 mengganggu).
  const bolehLookupProduk = can("inventory_produk", "view");
  // Pemilih IMEI per baris barang (brief bos poin 4: IMEI hanya boleh
  // DIPILIH dari unit yang terdaftar di inventory, bukan diketik bebas).
  // Modul beda lagi dari inventory_produk — kalau tidak diizinkan, baris
  // ber-SKU tetap bisa disimpan TANPA IMEI (backend tidak pernah memaksa
  // IMEI terisi), pemilihnya sekadar tidak muncul.
  const bolehLookupUnit = can("inventory_unit", "view");
  // Cetak faktur/struk DIHAPUS dari halaman ini (brief bos 21 Agu 2026 #1) —
  // nota yang sudah di-approve otomatis jadi dokumen `sales` dan tercetak
  // dari Input Penjualan (lihat link petunjuk di header di bawah). Dipakai
  // untuk gating link petunjuk itu supaya tidak mengarah ke halaman yang
  // pengguna sendiri tidak punya izin membukanya.
  const bolehLihatPenjualan = can("sales", "view");
  // Sumber Info (brief bos 11 Agu 2026) — saklar darurat + pilihan + daftar
  // KOL/PFP aktif diambil SEKALI di sini (bukan tiap modal dibuka/tiap
  // ketikan), lihat docstring useSourceInfoConfig.
  const sourceInfoCfg = useSourceInfoConfig();

  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [showMine, setShowMine] = useState(!canApprove);
  const [summary, setSummary] = useState({ pending: 0, approved: 0, rejected: 0 });

  const [modal, setModal] = useState(false);
  // Paket/bundling (poin 8, brief bos 23 Agu 2026) — resep AKTIF (+ stok
  // virtualnya) dimuat ulang tiap modal dibuka, sama pola & alasan dengan
  // Sales.jsx (lihat docstring BundleKomponenPicker.jsx).
  const { resepMap: resepBundlingMap } = useResepBundlingAktif(bolehLookupProduk && modal);
  const { produkInfoCache, ensureProdukInfo } = useProdukInfoCache(bolehLookupProduk);
  const [modalMode, setModalMode] = useState("create");
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  // Antrian (brief 15 Sep G5/G6). Hanya pemegang antrian.create yang bisa
  // memindai tiket, jadi hanya mereka yang pernah ditanyai status wajib-scan —
  // selain itu halaman ini berperilaku persis seperti dulu.
  const bolehAntrian = can("antrian", "create");
  const bolehBukaAntrian = bolehAntrian || can("antrian", "view");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const kodeDariUrl = searchParams.get("kode_antrian");
  const [statusAntrian, setStatusAntrian] = useState(null);
  const wajibScan = !!statusAntrian?.wajib_scan;
  const [memeriksaAntrian, setMemeriksaAntrian] = useState(false);
  const [konfirmasiTiket, setKonfirmasiTiket] = useState(null); // {pilihan: [...], kode}
  const [scanInline, setScanInline] = useState(false);
  const [pesanTolakAntrian, setPesanTolakAntrian] = useState("");
  const [kodeInline, setKodeInline] = useState("");
  const [busyPanggil, setBusyPanggil] = useState(false);
  // ?kode_antrian hanya boleh membuka modal SEKALI. Tanpa ref, setiap kali
  // daftar karyawan dimuat ulang modalnya terbuka lagi dan menimpa isian.
  const sudahBukaDariUrl = useRef(false);
  const nomorTiketForm = nomorUntukKode(statusAntrian, form.kode_antrian);

  useEffect(() => {
    if (!bolehAntrian) return;
    let batal = false;
    ambilTiketSaya().then((st) => { if (!batal) setStatusAntrian(st); });
    return () => { batal = true; };
  }, [bolehAntrian]);

  // N1 (brief 22 Sep 2026) — identitas pelanggan. Aturannya CERMIN server
  // (`routers/identitas_nota.py`): Cabang & No HP wajib saat nota BARU atau
  // saat medannya DIUBAH (nota lama tanpa HP tetap bisa diedit selama HP-nya
  // tidak disentuh), dan nama B2B/USER bertempo wajib dipilih dari kartu
  // pelanggan. Dihitung dari `form`, jadi ikut Jenis/Tempo tanpa state kedua.
  const aturanIdentitas = useAturanIdentitasNota();
  const identitasAwal = form._identitas_awal;
  const hpBerubah = !identitasAwal || hpBerubahLib(identitasAwal.no_hp, form.no_hp);
  const cabangBerubah = !identitasAwal
    || (identitasAwal.branch || "").trim().toLowerCase() !== (form.branch || "").trim().toLowerCase();
  const cekHp = periksaBentukHp(form.no_hp);
  const galatHp = !hpBerubah ? null
    : (!cekHp.sah ? cekHp.pesan
      : (!cekHp.simpan && aturanIdentitas.hp_cabang_wajib
        ? "No HP pelanggan wajib diisi (HP Indonesia 10–13 angka, contoh 0812 3456 7890)." : null));
  // Kolom HP yang masih KOSONG tidak langsung dimerahkan saat form dibuka —
  // kewajibannya disampaikan saat Kirim; yang ditampilkan seketika hanya
  // bentuk nomor yang salah ketik.
  const galatHpTampil = (form.no_hp || "").trim() ? galatHp : null;
  const galatCabang = cabangBerubah && aturanIdentitas.hp_cabang_wajib && !(form.branch || "").trim()
    ? "Cabang wajib dipilih." : null;
  const perluKartu = butuhKartu(form, identitasAwal, aturanIdentitas);
  // Memilih kartu: nama = ejaan kartu (satu kunci Tagih Sekaligus). No HP kartu
  // hanya MENGISI kolom yang masih kosong — pelanggan B2B boleh memakai nomor
  // yang tercatat di kartu lain, dan kartu lama bisa menyimpan nomor tak sah.
  const pilihKartu = (k) => setForm(f => {
    const hpKartu = hpTampilan(k.no_hp);
    const isiHp = !(f.no_hp || "").trim() && hpKartu && periksaBentukHp(hpKartu).simpan;
    return { ...f, nama_pelanggan: k.nama, _kartu: { nama: k.nama },
             no_hp: isiHp ? hpKartu : f.no_hp };
  });
  // Dialog konfirmasi PPN sebelum simpan (brief bos) — menyimpan payload yang
  // SUDAH divalidasi/dibersihkan, menunggu klik "Lanjutkan". null = tidak tampil.
  const [ppnConfirm, setPpnConfirm] = useState(null);

  const [detail, setDetail] = useState(null);
  // Nota yang sedang dikonfirmasi Total Pembayarannya (jendela approve, N3 D6).
  const [konfirmasiApprove, setKonfirmasiApprove] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [aiPanel, setAiPanel] = useState(false); // panel chat AI (hanya relevan saat modal terbuka)
  // PIN persetujuan hapus (brief #23) — retry() menyimpan aksi delete yang
  // sempat dibalas 428, dipanggil ulang begitu PIN dikirim.
  const [pinDialog, setPinDialog] = useState(null); // {pin, submitting, error, retry}

  // Employees + payment methods (loaded once)
  const [employees, setEmployees] = useState([]);
  // Penanda "permintaan karyawan sudah selesai" (berhasil ATAU gagal). Modal
  // dari ?kode_antrian menunggu ini supaya nama sales terisi — tapi tidak
  // boleh menunggu selamanya kalau daftarnya memang kosong/ditolak.
  const [employeesTermuat, setEmployeesTermuat] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);     // active-only (untuk dropdown form)
  const [paymentGroups, setPaymentGroups] = useState({});       // active-only grouped
  const [paymentGroupsAll, setPaymentGroupsAll] = useState({});
  const [methodsModal, setMethodsModal] = useState(false);

  // Restrict nama_penjual choice — my_only vs all_employees
  const [penjualScope, setPenjualScope] = useState("mine"); // "mine" | "all"

  // Pengurutan tabel daftar nota (client-side, lihat components/ui/sort.jsx) —
  // urutan bawaan tetap seperti sebelumnya (backend sudah `.sort("created_at",
  // -1).limit(500)`, jadi sort di sini HANYA menata ulang 500 nota terbaru
  // yang sudah termuat, bukan seluruh data kalau nota lebih dari itu).
  const notaSort = useSortState(null, "asc");

  // Filter daftar nota (brief owner 29 Agu, poin 2-3). Semuanya dikirim ke
  // SERVER: daftarnya dipotong 500 baris, jadi menyaring di layar akan
  // melewatkan nota lama tanpa satu pun tanda.
  const [fDari, setFDari] = useState("");
  const [fSampai, setFSampai] = useState("");
  const [fKategori, setFKategori] = useState("");
  const [fCabang, setFCabang] = useState("");
  const [fBrand, setFBrand] = useState("");
  const [opsiCabang, setOpsiCabang] = useState([]);
  const [opsiBrand, setOpsiBrand] = useState([]);

  const load = () => {
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    if (showMine) p.set("mine", "true");
    if (fDari) p.set("dari", fDari);
    if (fSampai) p.set("sampai", fSampai);
    if (fKategori) p.set("kategori", fKategori);
    if (fCabang) p.set("branch", fCabang);
    if (fBrand) p.set("brand", fBrand);
    api.get(`/nota/sales?${p}`)
      .then(r => setItems(r.data || []))
      .catch(e => {
        const pesan = e?.response?.data?.detail;
        toast.error(e?.response?.status === 400 && pesan ? pesan : "Gagal memuat nota");
      });
    api.get("/nota/sales/summary/stats").then(r => setSummary(r.data || {}));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [status, showMine, fDari, fSampai, fKategori, fCabang, fBrand]);

  useEffect(() => {
    api.get("/finance/branches/master")
      .then(r => setOpsiCabang((r.data?.items || []).map(b => b.name || b)))
      .catch(() => { /* dropdown kosong; filter cabang tinggal tidak terpakai */ });
    api.get("/inventory/master-opsi")
      .then(r => {
        const m = r.data?.master_opsi || r.data || {};
        const merek = [...(m.brand_gadget || []), ...(m.brand_accessories || []), ...(m.brand || [])];
        setOpsiBrand([...new Set(merek)].sort());
      })
      .catch(() => { /* merek bebas diketik lewat kolom teks di bawah */ });
  }, []);

  useEffect(() => {
    api.get("/employees")
      .then(r => setEmployees(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => { /* nama sales tinggal diketik manual seperti sebelumnya */ })
      .finally(() => setEmployeesTermuat(true));
    loadMethods();
  }, []);

  // Kredit retur milik pelanggan yang sedang diketik — sejak brief 22 Sep 2026
  // (I1) milik pasangan nama DAN No HP, jadi petunjuknya membaca kedua kolom.
  // Satu sumber dengan Input Penjualan (hooks/useKreditRetur).
  const kreditInfo = useKreditRetur(form.nama_pelanggan, form.no_hp);
  const kreditPelanggan = kreditInfo.sisa;

  const loadMethods = () => {
    // Active-only untuk dropdown pemilihan di form
    api.get("/payment-methods?active_only=true").then(r => {
      setPaymentMethods(r.data?.items || []);
      setPaymentGroups(r.data?.groups || {});
    });
    // Full list (termasuk inactive) untuk Manager modal — inactive rows tampil greyed
    api.get("/payment-methods?active_only=false").then(r => {
      setPaymentGroupsAll(r.data?.groups || {});
    });
  };

  const totalHarga = useMemo(() => form.items.reduce((s, i) => s + (parseFloat(i.harga) || 0), 0), [form.items]);
  // Paket/bundling (poin 8-9, brief bos 23 Agu 2026, aturan #3 CLAUDE.md) —
  // alasan (kalau ada) kenapa tombol "Kirim ke Kasir"/"Simpan Perubahan"
  // masih ditahan. null = semua baris paket (kalau ada) sudah siap dikirim.
  const bundleAlasanTertahan = useMemo(
    () => cariAlasanBundleBelumSiap(form.items, resepBundlingMap, produkInfoCache),
    [form.items, resepBundlingMap, produkInfoCache]
  );
  // N2: diskon = jumlah diskon per barang, atau diskon nota LAMA draf yang
  // dibuka (tidak pernah keduanya) — SATU nilai untuk DPP dan rincian di bawah.
  const diskonForm = useMemo(
    () => diskonEfektifForm({ items: form.items, _diskon_lama: form._diskon_lama }),
    [form.items, form._diskon_lama],
  );
  // PPN (keputusan owner 18 Agu 2026) — dpp tingkat DOKUMEN (item yang diketik
  // kasir sudah exclusive PPN). Sejak 25 Sep 2026 (26f) DPP = total SESUDAH
  // diskon, lewat `dppNota` (cermin `pajak_nota` server). Pratinjau saja —
  // lihat komentar PPN_PERSEN_PREVIEW.
  const dpp = useMemo(() => dppNota(totalHarga, diskonForm), [totalHarga, diskonForm]);
  // Dibulatkan SEPERTI server (Python: .5 ke genap) — lihat lib/rincianBayar.js.
  const ppnPreview = useMemo(
    () => form.kena_ppn ? hitungPpn(dpp, PPN_PERSEN_PREVIEW) : 0,
    [form.kena_ppn, dpp]
  );
  const totalDenganPpn = dpp + ppnPreview;
  const tradeInTotal = useMemo(
    () => form.trade_in ? (form.trade_in_items || []).reduce((s, t) => s + (parseFloat(t.harga) || 0), 0) : 0,
    [form.trade_in, form.trade_in_items]
  );
  // Total nota - metode pembayaran - trade-in must equal 0 (valid)
  // DP pelanggan (brief owner 29 Agu, diperbaiki 12 Sep). Dicari lewat NOMOR
  // HP **atau** NAMA: owner meminta pengguna "memilih nama pelanggan dari
  // daftar DP yang tersedia", dan nomor HP saja tidak cukup — DP kerap disetor
  // dengan nomor yang ditulis beda format, atau tanpa nomor sama sekali.
  // Dimuat setelah jeda, bukan setiap ketikan, supaya tidak menembak server
  // tiap huruf.
  const [dpTersedia, setDpTersedia] = useState([]);
  const [dpDicari, setDpDicari] = useState(false);
  useEffect(() => {
    const hp = (form.no_hp || "").trim();
    const nama = (form.nama_pelanggan || "").trim();
    // Ambang berbeda: nomor HP baru bermakna setelah 6 digit, nama setelah 3
    // huruf. Di bawah itu daftarnya akan berisi pelanggan orang lain.
    const pakaiHp = hp.length >= 6;
    const pakaiNama = nama.length >= 3;
    if (!pakaiHp && !pakaiNama) { setDpTersedia([]); setDpDicari(false); return; }
    let batal = false;
    const t = setTimeout(async () => {
      try {
        const params = {};
        if (pakaiHp) params.no_hp = hp;
        if (pakaiNama) params.nama = nama;
        const r = await api.get("/down-payment/tersedia", { params });
        if (!batal) { setDpTersedia(r.data?.items || []); setDpDicari(true); }
      } catch { if (!batal) { setDpTersedia([]); setDpDicari(true); } }
    }, 400);
    return () => { batal = true; clearTimeout(t); };
  }, [form.no_hp, form.nama_pelanggan]);

  // Rincian nota TANPA DP — rumus yang SAMA dengan server (lib/rincianBayar.js
  // = cermin `sales.rincian_bayar`, dijaga tes kesetaraan). Dulu layar ini
  // menghitung angkanya sendiri (total + PPN sebelum diskon, tanpa admin) dan
  // angka yang disarankannya ditolak server.
  // N2: diskon = jumlah diskon per barang, atau diskon nota LAMA draf yang
  // dibuka (tidak pernah keduanya); biaya admin hanya dari draf lama.
  const diskonBarisForm = useMemo(() => diskonBarisTotal(form.items), [form.items]);
  const rincianTanpaDp = useMemo(() => {
    const f = { items: form.items, _diskon_lama: form._diskon_lama };
    const diskon = diskonForm;
    const { mode, nilai } = adminEfektifForm(f);
    const admin = hitungBiayaAdmin(bulatkanSepertiPython(totalHarga - diskon, 2), mode, nilai);
    return rincianBayar({ total: totalHarga, diskon, biayaAdmin: admin, ppn: ppnPreview, tradeIn: tradeInTotal });
  }, [totalHarga, form.items, form._diskon_lama, diskonForm, ppnPreview, tradeInTotal]);

  // DOWNGRADE (brief poin 6): unit trade-in lebih tinggi dari total belanja.
  // Selisihnya bukan "kembalian" — ia UTANG perusahaan kepada pelanggan, dan
  // dibayarkan lewat transfer, jadi rekeningnya harus diketahui.
  const selisihDowngrade = Math.max(0, -rincianTanpaDp.harusDibayar);

  // Tiga angka yang diminta owner: saldo DP, nominal yang dipakai, sisa DP.
  const dpTerpilih = useMemo(
    () => (form.dp_id ? dpTersedia.find(x => x.id === form.dp_id) || null : null),
    [form.dp_id, dpTersedia],
  );
  const dpDipakai = useMemo(() => {
    if (!dpTerpilih) return 0;
    // Kosong = pakai seluruh sisa; itu yang paling sering diinginkan kasir dan
    // aturan yang SAMA dengan backend, bukan tebakan layar sendiri.
    // Batasnya = yang HARUS dibayar (sesudah diskon/admin/PPN/trade-in), sama
    // dengan server (`batas_dp_nota`) — bukan total bruto.
    const minta = Number(form.dp_nominal) || Math.min(dpTerpilih.sisa, Math.max(0, rincianTanpaDp.harusDibayar));
    return Math.min(minta, dpTerpilih.sisa);
  }, [dpTerpilih, form.dp_nominal, rincianTanpaDp.harusDibayar]);
  const dpSaldo = dpTerpilih ? Number(dpTerpilih.sisa) || 0 : 0;
  const dpSisaSetelah = Math.max(0, dpSaldo - dpDipakai);
  // DP yang sudah tidak ada di daftar hasil pencarian TIDAK boleh tetap
  // terpilih. Mengganti nama/nomor pelanggan setelah memilih DP membuat
  // daftarnya berubah, tapi `form.dp_id` tidak pernah dibersihkan — layarnya
  // bahkan menulis "Tidak ada DP tersisa…" sementara payload-nya masih
  // membawa dp_id lama dan (karena `dp_nominal` kosong = seluruh sisa)
  // menghabiskan uang muka pelanggan yang SALAH.
  useEffect(() => {
    if (form.dp_id && !dpTersedia.some(d => d.id === form.dp_id)) {
      setForm(f => ({ ...f, dp_id: "", dp_nominal: "" }));
    }
  }, [dpTersedia, form.dp_id]);

  // SATU rincian untuk seluruh layar (batch UANG-KASIR N0): angka "diterima di
  // kasir" (`diTempat`) = harus dibayar − DP. Itulah acuan baris pembayaran
  // utama (N3) dan petunjuk DP — server menghitung uangnya dari komponen yang
  // sama, jadi angka ini selalu lolos.
  const rincianLayar = useMemo(
    () => rincianBayar({ total: rincianTanpaDp.total, diskon: rincianTanpaDp.diskon,
      biayaAdmin: rincianTanpaDp.biayaAdmin, ppn: rincianTanpaDp.ppn,
      tradeIn: rincianTanpaDp.tradeIn, dp: dpDipakai }),
    [rincianTanpaDp, dpDipakai],
  );
  // Baris pembayaran (batch N3, D4). Baris UTAMA nota tunai mengikuti sisa
  // tagihan (angka dari rincian yang sama dengan server) sampai kasir
  // mengetiknya sendiri; nota bertempo tidak pernah diikutkan (uang muka).
  const bertempo = !!form.tempo_pembayaran;
  useEffect(() => {
    setForm(f => {
      const baru = ikutkanSisa(f.payments, { diTempat: rincianLayar.diTempat, tempo: !!f.tempo_pembayaran });
      return baru === f.payments ? f : { ...f, payments: baru };
    });
  }, [rincianLayar.diTempat, form.tempo_pembayaran, form.payments]);
  const galatBayar = galatBarisPembayaran(form.payments, rincianLayar, { tempo: bertempo });
  // Kredit retur dipotong dari saldo pelanggan saat approve — tampilkan saldo
  // bila ada baris Kredit Retur supaya kasir tahu cukup-tidaknya.
  const pakaiKreditRetur = (form.payments || []).some(p => adalahKreditReturMetode(p.method)
    && (Number(p.amount) || 0) > 0);

  // Live clock — updates form.waktu every minute so it always reflects server time
  useEffect(() => {
    if (!modal || modalMode !== "create") return;
    const tick = () => setForm(f => ({ ...f, waktu: new Date().toTimeString().slice(0, 5) }));
    tick();
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, [modal, modalMode]);

  const availableEmployees = useMemo(() => {
    if (penjualScope === "mine") {
      // Prefer email match (more robust). Fallback to name.
      const byEmail = employees.filter(e => user?.email && e.email === user.email);
      if (byEmail.length) return byEmail;
      return employees.filter(e => e.nama === user?.name);
    }
    return employees;
  }, [penjualScope, employees, user]);

  const bukaModalCreate = (kode) => {
    // Default nama_penjual = user's own employee record (match by email first, then name)
    const me = employees.find(e => e.email === user?.email) || employees.find(e => e.nama === user?.name);
    setForm({
      ...emptyForm(),
      nama_penjual: me?.nama || user?.name || "",
      // Ejaan cabang karyawan disamakan dengan pilihan dropdown (N1) — tanpa
      // ini yang terkirim adalah ketikan data karyawan apa adanya, bukan
      // pilihan dropdown yang terlihat terpilih di layar.
      branch: cabangKanonikDariOpsi(me?.branch, BRANCHES),
      // Kode WAJIB diisi di sini, bukan sebelum fungsi ini dipanggil —
      // `...emptyForm()` di atas menghapus apa pun yang diset lebih dulu.
      kode_antrian: kode || "",
    });
    setScanInline(false);
    setPesanTolakAntrian("");
    setKodeInline("");
    setPenjualScope("mine");
    setModalMode("create");
    setModal(true);
  };

  /** Buka form nota baru. `opts.kode` = kode tiket antrian yang sudah pasti
   *  (dari URL / konfirmasi); tanpa itu, pengguna wajib-scan ditanya dulu
   *  tiket mana yang dipakai. Status diambil SAAT KLIK, bukan dari hasil mount:
   *  FL bisa baru saja menutup tiket di tab lain. */
  const openCreate = async (opts) => {
    const kode = typeof opts?.kode === "string" ? opts.kode.trim() : "";
    if (kode) { bukaModalCreate(kode); return; }
    if (!bolehAntrian) { bukaModalCreate(""); return; }
    setMemeriksaAntrian(true);
    const st = await ambilTiketSaya();
    setMemeriksaAntrian(false);
    if (st) setStatusAntrian(st);
    if (!st?.wajib_scan) { bukaModalCreate(""); return; }
    const pilihan = daftarTiketPilihan(st);
    if (pilihan.length) {
      setKonfirmasiTiket({ pilihan, kode: pilihan[0].kode_antrian });
      return;
    }
    toast.error("Scan nomor antrian pelanggan dulu");
    navigate("/antrian?tab=layani");
  };

  // Datang dari Antrian (tombol "Membeli" / "Buat nota"): buka form berkode
  // SESUDAH daftar karyawan termuat — openCreate mengisi nama sales dari
  // daftar itu — lalu buang parameternya supaya muat ulang halaman tidak
  // membuka form yang sama lagi.
  useEffect(() => {
    // Parameter sudah dibuang = siap menerima kode BARU (mis. kembali lewat
    // tombol "Buat nota" tanpa halaman ini sempat di-unmount).
    if (!kodeDariUrl) { sudahBukaDariUrl.current = false; return; }
    if (sudahBukaDariUrl.current || !employeesTermuat) return;
    sudahBukaDariUrl.current = true;
    openCreate({ kode: kodeDariUrl });
    setSearchParams(p => { p.delete("kode_antrian"); return p; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kodeDariUrl, employeesTermuat]);

  const pilihTiketKonfirmasi = () => {
    const kode = konfirmasiTiket?.kode;
    setKonfirmasiTiket(null);
    if (kode) bukaModalCreate(kode);
  };

  /** Panggil tiket dari DALAM modal setelah server menolak nota (409). Isian
   *  form tidak boleh hilang hanya karena tiketnya belum dipindai. */
  const panggilTiketInline = async (kodeMentah) => {
    const bersih = (kodeMentah || "").trim();
    if (!bersih) { toast.error("Kode antrian kosong"); return; }
    setBusyPanggil(true);
    try {
      const { data } = await api.post("/antrian/panggil", { kode_antrian: bersih });
      const kode = data?.item?.kode_antrian || bersih;
      setForm(f => ({ ...f, kode_antrian: kode }));
      setStatusAntrian(s => (s ? { ...s, tiket: data?.item || s.tiket } : s));
      setScanInline(false);
      setPesanTolakAntrian("");
      setKodeInline("");
      toast.success(`${data?.message || "Tiket terkunci"} — tekan Kirim ke Kasir lagi`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memanggil tiket");
    } finally {
      setBusyPanggil(false);
    }
  };

  const openEdit = (n) => {
    setForm({
      // `...emptyForm()` WAJIB di depan. Tanpa itu, setiap medan yang
      // ditambahkan ke formulir SESUDAH fungsi ini ditulis akan bernilai
      // undefined saat nota dibuka — lalu PATCH /nota/sales mengganti dokumen
      // secara UTUH, jadi medan itu terhapus dari nota tanpa satu pun pesan.
      // Sudah menggigit: mengedit nota bertempo membuang jenis_pelanggan,
      // tempo_pembayaran, dan pic_approval_piutang — notanya diam-diam
      // berubah jadi Pelanggan Umum dan piutangnya tidak pernah lahir.
      ...emptyForm(),
      id: n.id,
      tanggal: n.tanggal, waktu: n.waktu,
      nama_pelanggan: n.nama_pelanggan || "", no_hp: n.no_hp || "",
      // Nota dari SEBELUM field kategori ada (bukan alias lama — itu kunci
      // yang sah apa adanya): tebak sekali dari teks sebagai cadangan
      // terakhir supaya dropdown tidak kosong.
      // Pemetaan baris barang dokumen -> form (SKU, IMEI, bonus, komponen
      // paket, dan diskon per barang N2) SATU sumber untuk Input Nota & Input
      // Penjualan: `lib/payloadNota.js::itemsFormDariDokumen`, diuji pulang-
      // pergi terhadap respons GET sungguhan. Baris sintetik komponen paket
      // (hanya lahir di `sales`, bukan draf) disaring di sana sebagai jaring
      // pengaman. Kategori dokumen lama yang kosong ditebak dari teks.
      items: itemsFormDariDokumen(n.items?.length ? n.items : [{ nama_barang: "", harga: 0, kode: "HP" }],
        deriveKategoriTeksBebas),
      imei: n.imei || "", metode_pembayaran: n.metode_pembayaran || "Cash",
      is_split: !!(n.payments && n.payments.length > 1),
      // Baris pembayaran (N3): draf bertanda apa adanya (keterangan &
      // metode_id ikut); draf LAMA dikonversi (split → barisnya, bertempo →
      // uang muka dari Harga Terima, tunai → mengikuti sisa) — satu sumber di
      // lib/payloadNota.js supaya uang muka draf lama tidak lenyap saat disimpan.
      payments: barisPembayaranDariDokumen(n),
      _uang_muka_lama: uangMukaBentukLama(n),
      trade_in: !!n.trade_in, trade_in_note: n.trade_in_note || "",
      // Support both new (trade_in_items[]) and legacy (trade_in_note string) formats
      trade_in_items: n.trade_in_items?.length
        ? n.trade_in_items.map(t => ({
            nama: t.nama || "", status: t.status || "", warna: t.warna || "",
            bh: t.bh || "", kelengkapan: t.kelengkapan || "",
            imei: t.imei || "", catatan: t.catatan || "", harga: t.harga || 0 }))
        : (n.trade_in && n.trade_in_note
            ? [{ nama: n.trade_in_note, status: "", warna: "", bh: "", kelengkapan: "",
                 imei: "", catatan: "", harga: 0 }]
            : [{ nama: "", status: "", warna: "", bh: "", kelengkapan: "",
                 imei: "", catatan: "", harga: 0 }]),
      // Harga Terima tidak dimuat lagi: uang muka draf lama sudah pindah ke
      // baris pembayaran (di atas), dan form N3 mengirim 0.
      harga_terima: 0, catatan: n.catatan || "",
      // Total Pembayaran yang pernah diisi KASIR INI di form draf versi ini.
      total_pembayaran_kasir: (n.total_pembayaran_kasir_oleh_id === user?.id
        && n.total_pembayaran_kasir !== null && n.total_pembayaran_kasir !== undefined)
        ? n.total_pembayaran_kasir : "",
      nama_penjual: n.nama_penjual || "", branch: n.branch || "",
      // Nota lama (sebelum fitur PPN ada) tidak punya field ini -> default
      // false, kompatibel mundur.
      kena_ppn: !!n.kena_ppn,
      // Nota lama (sebelum fitur Sumber Info ada, 11 Agu 2026) tidak punya
      // field ini -> "" (kosong), backend TIDAK memaksa diisi ulang kalau
      // PATCH ini sama sekali tidak menyentuhnya (lihat komentar di
      // routers/sales.py update_sale). Kalau kasir memang mengosongkannya di
      // sini, itu perubahan eksplisit dan tunduk saklar darurat seperti biasa.
      source_info: n.source_info || "", source_ref_id: n.source_ref_id || "",
      // Medan brief 29 Agu yang sebelumnya hilang saat edit. Semua pakai
      // fallback ke bawaan emptyForm() supaya nota LAMA (yang memang tidak
      // punya medan ini) terbuka persis seperti dulu.
      jenis_pelanggan: n.jenis_pelanggan || "umum",
      tempo_pembayaran: n.tempo_pembayaran || "",
      pic_approval_piutang: n.pic_approval_piutang || "",
      // N2: PIC diskon satu per nota; diskon nota & biaya admin LAMA hanya
      // dipegang sebagai potret baca-saja — payload tidak mengirimnya kecuali
      // kasir menekan "Hapus diskon lama" (server mempertahankan nilai
      // tersimpan yang tidak dikirim).
      diskon_pic_approval: n.diskon_pic_approval || "",
      _diskon_lama: ambilDiskonLama(n, { draf: true }),
      dp_id: n.dp_id || "",
      dp_nominal: n.dp_nominal ?? "",
      refund_nama_bank: n.refund_nama_bank || "",
      refund_no_rekening: n.refund_no_rekening || "",
      refund_atas_nama: n.refund_atas_nama || "",
      // Hanya untuk spanduk baca-saja: backend mengabaikan kode_antrian di
      // PATCH, tapi menyalinnya tetap membuat payload membawa nilai aslinya.
      kode_antrian: n.kode_antrian || "",
      // N1: potret identitas saat dibuka — aturan "wajib" hanya berlaku untuk
      // medan yang DIUBAH (cermin server), jadi draf lama tanpa HP/cabang
      // tetap bisa disimpan setelah harganya dibetulkan.
      _kartu: null,
      _identitas_awal: {
        nama_pelanggan: n.nama_pelanggan || "", no_hp: n.no_hp || "", branch: n.branch || "",
        jenis_pelanggan: n.jenis_pelanggan || "umum", tempo_pembayaran: n.tempo_pembayaran || "",
      },
    });
    setScanInline(false);
    setPesanTolakAntrian("");
    // If penjual not in "mine" scope, auto-switch to "all"
    const inMine = employees.some(e => (e.nama === user?.name || e.email === user?.email) && e.nama === n.nama_penjual);
    setPenjualScope(inMine ? "mine" : "all");
    setModalMode("edit");
    setDetail(null);
    setModal(true);
  };

  const submit = async () => {
    if (!form.nama_pelanggan.trim()) { toast.error("Nama pelanggan wajib diisi"); return; }
    // Identitas pelanggan (N1) — cermin server, dicegat di sini supaya kasir
    // tahu langkahnya sebelum menekan "Kirim ke Kasir".
    if (galatCabang) { toast.error(galatCabang); return; }
    if (galatHp) { toast.error(galatHp); return; }
    if (perluKartu && !form._kartu) {
      toast.error("Nota B2B/USER bertempo: pilih nama pelanggan dari daftar Data Pelanggan, "
        + "atau tekan \"+ Pelanggan baru\" kalau belum ada.");
      return;
    }
    if (!form.nama_penjual.trim()) { toast.error("Nama sales wajib diisi"); return; }
    if (!form.items[0].nama_barang.trim()) { toast.error("Nama barang wajib diisi"); return; }
    // Sumber Info (brief bos 11 Agu 2026) — cermin persis aturan backend
    // (_validasi_dan_lengkapi_source_info) supaya kasir dicegat DI SINI,
    // bukan menabrak 400 dari server setelah menekan "Kirim ke Kasir".
    const errSourceInfo = validasiSourceInfo(form.source_info, form.source_ref_id, sourceInfoCfg);
    if (errSourceInfo) { toast.error(errSourceInfo); return; }
    // Backend menolak baris berharga Rp 0 kecuali ditandai bonus (brief bos) —
    // dicegat di sini dulu supaya sales tidak menabrak error 400 dari server
    // tanpa tahu jalan keluarnya.
    const itemGratisTanpaTanda = form.items.find(x => x.nama_barang.trim() && (parseFloat(x.harga) || 0) === 0 && !x.is_bonus);
    if (itemGratisTanpaTanda) {
      toast.error(`Harga "${itemGratisTanpaTanda.nama_barang}" masih Rp0 — centang "Bonus/Gratis" kalau barang ini memang diberikan cuma-cuma, atau isi harga jualnya.`);
      return;
    }
    // Satu unit tidak boleh terpilih di dua baris sekaligus DALAM SATU nota
    // ini (backend TIDAK menegakkan ini — `_tolak_stok_kosong` memvalidasi
    // tiap baris independen, lihat catatan di ItemImeiField). Ini pagar
    // terakhir sebelum terkirim; pemilihnya sendiri sudah menonaktifkan opsi
    // yang sama di baris lain, jadi ini seharusnya jarang tersentuh.
    const imeiTerpakai = form.items.map(x => (x.imei || "").trim()).filter(Boolean);
    const imeiDobel = imeiTerpakai.find((v, idx) => imeiTerpakai.indexOf(v) !== idx);
    if (imeiDobel) {
      toast.error(`IMEI ${imeiDobel} dipilih di lebih dari satu baris — satu unit hanya boleh dijual sekali dalam satu nota.`);
      return;
    }
    // Paket/bundling (poin 8-9, brief bos 23 Agu 2026) — aturan #3 CLAUDE.md:
    // jangan biarkan kasir menekan "Kirim ke Kasir" lalu menabrak 400 dari
    // backend. Tombol sudah ditahan (lihat `disabled` di bawah); ini jaring
    // pengaman terakhir untuk kasus race (mis. stok berubah tepat sebelum klik).
    const alasanBundle = cariAlasanBundleBelumSiap(form.items, resepBundlingMap, produkInfoCache);
    if (alasanBundle) { toast.error(alasanBundle); return; }
    // Baris pembayaran (batch N3) — aturan yang SAMA dengan server
    // (`terapkan_baris_pembayaran`): metode terisi, keterangan baris tambahan,
    // Σ baris + DP = yang harus dibayar (tunai), bertempo tidak dibayar penuh,
    // downgrade tanpa baris. Dulu split berselisih cukup `confirm()`, lalu
    // posting menambalnya diam-diam atau approve-nya macet.
    if (galatBayar) { toast.error(galatBayar); return; }
    // K27: draf LAMA bertempo yang dibuka layar ini — uang mukanya pindah ke
    // baris pembayaran. Kalau angkanya berubah, tanya dulu (server menolak
    // perubahan yang tidak dinyatakan): uang muka yang lenyap berarti pelanggan
    // ditagih ulang sebesar itu.
    let uangMukaDikonfirmasi = false;
    if (form._uang_muka_lama !== null && form._uang_muka_lama !== undefined && bertempo) {
      const baru = (form.payments || []).filter(p => !adalahKreditReturMetode(p.method))
        .reduce((a, p) => a + (Number(p.amount) || 0), 0);
      if (Math.abs(baru - form._uang_muka_lama) > 0.5) {
        if (!confirm(`Uang muka tersimpan di nota ini ${idr(form._uang_muka_lama)}, sekarang menjadi `
          + `${idr(baru)}. Benar uang mukanya berubah?`)) return;
        uangMukaDikonfirmasi = true;
      }
    }
    if (form.trade_in) {
      const validTradeItems = (form.trade_in_items || []).filter(t => t.nama.trim());
      if (validTradeItems.length === 0) { toast.error("Isi minimal 1 unit trade-in dengan nama"); return; }
      // Kolom wajib trade-in (brief owner 29 Agu, Input Nota poin 4). Daftarnya
      // SAMA persis dengan `validasi_trade_in_wajib` di backend — dicek di sini
      // hanya supaya pesannya menyebut kolom yang kosong, bukan sebagai
      // pengganti penjagaan server. BH sengaja tidak termasuk (brief: opsional).
      const wajibTI = [["Nama barang", "nama"], ["Status", "status"],
                       ["Warna", "warna"], ["Kelengkapan", "kelengkapan"],
                       ["IMEI/SN", "imei"]];
      for (let i = 0; i < validTradeItems.length; i++) {
        const kurang = wajibTI
          .filter(([, k]) => !String(validTradeItems[i][k] || "").trim())
          .map(([label]) => label);
        if (kurang.length) {
          toast.error(`Unit trade-in #${i + 1} — wajib diisi: ${kurang.join(", ")}`);
          return;
        }
      }
    }
    if (form.jenis_pelanggan === "user" && !(form.pic_approval_piutang || "").trim()) {
      toast.error("Jenis pelanggan USER wajib menyebut PIC Persetujuan Piutang"); return;
    }
    // Diskon per barang (N2) — cermin aturan server, pesan yang sama: isi
    // kolom yang tidak sah, campuran dengan diskon nota lama, dan PIC. Diskon
    // tanpa nama yang menyetujui = potongan yang tidak bisa ditanyakan ke
    // siapa pun saat diperiksa belakangan.
    const galatDiskon = galatDiskonBaris(form.items) || galatCampurDiskon(form);
    if (galatDiskon) { toast.error(galatDiskon); return; }
    if (diskonBarisForm > 0 && !(form.diskon_pic_approval || "").trim()) {
      toast.error("Diskon per barang wajib menyebut PIC yang menyetujuinya"); return;
    }
    // Sub-kolom WAJIB unit trade-in (brief poin 4). Nama unit second yang tidak
    // menyebut status/warna/kelengkapan tidak bisa dihargai kembali — itulah
    // sebab 85 produk second di katalog tidak punya kondisi sama sekali.
    if (form.trade_in) {
      for (const [i, t] of (form.trade_in_items || []).entries()) {
        if (!(t.nama || "").trim()) continue;      // baris kosong dilewati
        const kurang = [];
        if (!(t.status || "").trim()) kurang.push("Status");
        if (!(t.warna || "").trim()) kurang.push("Warna");
        if (!(t.kelengkapan || "").trim()) kurang.push("Kelengkapan");
        if (!(t.imei || "").trim()) kurang.push("IMEI/SN");
        if (kurang.length) {
          toast.error(`Unit trade-in #${i + 1}: ${kurang.join(", ")} wajib diisi`);
          return;
        }
      }
    }
    // Trade-in melebihi belanja = DOWNGRADE (brief poin 6-7): selisihnya harus
    // dibayarkan KE pelanggan, jadi rekening tujuannya wajib diketahui.
    if (selisihDowngrade > 0) {
      const kurang = [];
      if (!(form.refund_nama_bank || "").trim()) kurang.push("Nama Bank");
      if (!(form.refund_no_rekening || "").trim()) kurang.push("Nomor Rekening");
      if (!(form.refund_atas_nama || "").trim()) kurang.push("Atas Nama");
      if (kurang.length) {
        toast.error(`Transaksi downgrade — ${kurang.join(", ")} wajib diisi untuk pengembalian dana`);
        return;
      }
    }
    const payload = { ...form };
    delete payload._kartu;
    delete payload._identitas_awal;
    delete payload._diskon_lama;
    // Baris barang dengan `diskon_rp` eksplisit di SETIAP baris + bagian
    // diskon (penanda layar, PIC, dan diskon nota lama hanya bila dihapus) —
    // satu sumber dengan Input Penjualan (lib/payloadNota.js).
    payload.items = itemsKePayload(form.items);
    delete payload.diskon_pic_approval;
    Object.assign(payload, bagianDiskonPayload(form, "nota"));
    // Baris pembayaran N3 (penanda layar `skema_nota` 3 menimpa penanda diskon
    // N2, `harga_terima` 0): satu sumber dengan Input Penjualan.
    Object.assign(payload, bagianBayarPayload(form));
    delete payload._uang_muka_lama;
    if (uangMukaDikonfirmasi) payload.uang_muka_dikonfirmasi = true;
    // Total Pembayaran (D6) hanya dari kasir; kosong = null (server membedakan
    // "belum diisi" dari 0). FL tidak punya kolomnya — server juga membuangnya.
    payload.total_pembayaran_kasir = canApprove ? nilaiTotalKasir(form.total_pembayaran_kasir) : null;
    // Harga Terima tidak ada lagi di form ini (selalu 0); tanda semantik
    // ketikan tetap dinyatakan supaya server tidak pernah menafsirkan 0 itu
    // dengan aturan layar lama.
    payload.harga_terima_diketik = true;
    // Kosong dikirim sebagai null, bukan "" — backend membedakan "tanpa tempo"
    // dari nilai yang tidak dikenal.
    payload.tempo_pembayaran = form.tempo_pembayaran || null;
    payload.pic_approval_piutang = (form.pic_approval_piutang || "").trim() || null;
    payload.dp_id = form.dp_id || null;
    // KOSONG dikirim sebagai `null`, BUKAN 0. Backend membaca None sebagai
    // "pakai seluruh sisa DP" dan menolak nol — jadi versi lama yang mengirim
    // 0 membuat pilihan "kosongkan = pakai seluruh sisa" SELALU gagal 400,
    // dan gagalnya baru muncul saat nota di-approve orang lain.
    // N3: nominal DP selalu PASTI (baris pembayaran dihitung terhadapnya) —
    // kolom kosong = "seluruh sisa" dihitung layar dengan aturan server yang
    // sama (`dpDipakai`), lalu dikirim sebagai angka.
    payload.dp_nominal = form.dp_id
      ? (String(form.dp_nominal ?? "").trim() === "" ? (dpDipakai || null) : Number(form.dp_nominal))
      : null;
    payload.refund_nama_bank = (form.refund_nama_bank || "").trim() || null;
    payload.refund_no_rekening = (form.refund_no_rekening || "").trim() || null;
    payload.refund_atas_nama = (form.refund_atas_nama || "").trim() || null;
    payload.kode_antrian = (form.kode_antrian || "").trim() || null;
    if (!form.trade_in) {
      payload.trade_in_items = null;
      payload.trade_in_note = "";
    } else {
      // Clean out empty rows before submit
      // Nama yang DISIMPAN adalah bentuk tergabungnya (brief poin 5) —
      // "IPHONE 13 128GB / RESMI / STARLIGHT / 85% / FULLSET ORI". Kalau yang
      // tersimpan cuma nama dasarnya, kondisi unitnya hilang begitu notanya
      // ditutup, dan barang second itu masuk gudang tanpa keterangan apa pun
      // (persis masalah 85 unit second tanpa kondisi, brief 23 Agu).
      // Sub-kolomnya TETAP ikut dikirim supaya bisa dibaca terpisah nanti.
      payload.trade_in_items = (form.trade_in_items || [])
        .filter(t => (t.nama || "").trim())
        .map(t => ({ ...t, nama: rakitNamaTradeIn(t), imei: (t.imei || "").trim() }));
      // Backward compat: sync trade_in_note as summary text
      payload.trade_in_note = payload.trade_in_items
        .map(t => `${t.nama}${t.imei ? ` [IMEI ${t.imei}]` : ""}${t.harga ? ` (${idr(t.harga)})` : ""}`)
        .join(", ");
    }
    // PPN (brief bos): dialog konfirmasi sebelum simpan — supaya kasir sadar
    // nota ini akan menambahkan PPN sebagai kewajiban pajak (2400 PPN
    // Keluaran) sebelum benar-benar terkirim, bukan baru sadar setelah dicetak.
    if (form.kena_ppn) {
      setPpnConfirm(payload);
      return;
    }
    await doSave(payload);
  };

  const doSave = async (payload) => {
    setSaving(true);
    try {
      const { data: tersimpan } = modalMode === "create"
        ? await api.post("/nota/sales", payload)
        : await api.patch(`/nota/sales/${form.id}`, payload);
      toast.success(modalMode === "create" ? "Nota terkirim — menunggu approval kasir" : "Nota diperbarui");
      // Kredit Retur nama + No HP ini belum ada / belum cukup (I1). Draf TETAP
      // tersimpan — tukar unit satu kunjungan menyimpan draf sebelum SPV
      // menyetujui returnya — tapi kasir perlu tahu SEKARANG bahwa approve akan
      // ditolak sampai kreditnya ada, bukan mendengarnya dari approver belakangan.
      if (tersimpan?.peringatan_kredit_retur) {
        toast.warning(tersimpan.peringatan_kredit_retur, { duration: 12000 });
      }
      setModal(false);
      setPpnConfirm(null);
      // FL wajib-scan bekerja dari layar Antrian: kembalikan ke sana supaya
      // pelanggan berikutnya langsung bisa dipindai.
      if (modalMode === "create" && payload.kode_antrian && wajibScan) {
        navigate("/antrian?tab=layani");
        return;
      }
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (modalMode === "create" && e?.response?.status === 409 && bolehAntrian) {
        // Modal TETAP terbuka: isian panjang (barang, IMEI, trade-in) tidak
        // boleh hilang hanya karena tiketnya belum dipindai. Status dimuat
        // ulang karena 409 bisa juga berarti tiketnya sudah dilepas SPV.
        const pesan = typeof detail === "string" ? detail : "Nota ditolak aturan antrian";
        toast.error(pesan);
        setPpnConfirm(null);
        const st = await ambilTiketSaya();
        if (st) setStatusAntrian(st);
        // Panel scan HANYA untuk penolakan yang memang soal antrian. Pengguna
        // wajib-scan yang SUDAH membawa kode bisa kena 409 karena hal lain
        // (mis. IMEI unitnya baru saja terjual di nota lain) — menyodorkan panel
        // scan di situ menyuruhnya memindai ulang tiket yang tidak bermasalah,
        // sementara masalah sebenarnya tenggelam di bawah panel.
        const soalAntrian = !payload.kode_antrian || /antrian/i.test(pesan);
        if (st?.wajib_scan && soalAntrian) {
          setPesanTolakAntrian(pesan);
          setScanInline(true);
        }
      } else {
        toast.error(detail || "Gagal simpan nota");
      }
    }
    finally { setSaving(false); }
  };

  // D6 (batch N3): approve SELALU lewat jendela Total Pembayaran — ketiga
  // tombol (baris daftar desktop & ponsel, detail) hanya membuka jendela ini.
  // Approve satu-klik tanpa angka sengaja dihapus: tombol yang mengirim POST
  // tanpa Total Pembayaran akan berujung 400 untuk draf baru (aturan #3), dan
  // untuk draf lama melewati kontrol kasir diam-diam.
  const approve = async (n, totalPembayaran) => {
    let data;
    try {
      ({ data } = await api.post(`/nota/sales/${n.id}/approve`,
        { total_pembayaran_kasir: totalPembayaran }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal approve nota");
      // Diteruskan ke jendela konfirmasi: alert-nya tetap di layar bersama
      // kolom angka, supaya kasir bisa menghitung ulang tanpa membuka ulang.
      throw e;
    }
    const saleId = data.sale_id;
    // Cetak dipindah ke Input Penjualan (brief bos 21 Agu 2026 #1) — toast
    // ini hanya mengonfirmasi transfer, tidak lagi menawarkan aksi cetak.
    toast.success(`✓ Nota disetujui & ditransfer ke Input Penjualan (ID: ${saleId?.slice(0, 8)}...)`);
    // Peringatan non-blokir "dijual di bawah modal" (brief bos 21 Agu #B1)
    // — nota adalah jalur kasir yang sesungguhnya, jadi tunduk kontrak yang
    // SAMA dengan Sales.jsx (lihat doSaveSale di sana): tidak menyebut
    // angka modal, hanya nama/SKU barangnya.
    (data?.sale?.peringatan_di_bawah_modal || []).forEach(p =>
      toast.warning(`${p.nama_barang || p.sku} dijual di bawah modal`, { duration: 8000 }));
    // Posting ke buku besar yang gagal TIDAK lagi senyap (N0): notanya sah
    // tersimpan, tapi Finance perlu menindaklanjuti.
    if (data?.sale?.posting_ledger?.status === "gagal") {
      toast.warning(`Nota tersimpan, tetapi BELUM masuk buku besar: ${data.sale.posting_ledger.alasan || "-"} `
        + "Finance melihatnya di Audit › Nota Belum Terposting.", { duration: 12000 });
    }
    setKonfirmasiApprove(null);
    setDetail(null);
    load();
  };

  const openReject = (n) => { setRejectModal(n); setRejectReason(""); };
  const doReject = async () => {
    if (!rejectReason.trim()) { toast.error("Alasan wajib diisi"); return; }
    try {
      await api.post(`/nota/sales/${rejectModal.id}/reject`, { reason: rejectReason.trim() });
      toast.success("Nota ditolak");
      setRejectModal(null); setDetail(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal reject"); }
  };

  const doDeleteNota = async (id, pin) => {
    await api.delete(`/nota/sales/${id}`, pin ? { params: { pin } } : undefined);
    toast.success("Nota dihapus");
    setDetail(null);
    load();
  };

  // Brief #23 — DELETE bisa membalas 428 (butuh PIN persetujuan pemegang izin
  // Pusat Approval). Ajukan otomatis, lalu buka dialog PIN.
  const deleteNota = async (n) => {
    if (!confirm(`Hapus nota ini?`)) return;
    try {
      await doDeleteNota(n.id);
    } catch (e) {
      if (e?.response?.status === 428) {
        try {
          await api.post("/aktivitas/hapus/ajukan", {
            modul: "nota_sales", target_id: n.id,
            ringkasan: `Hapus nota penjualan ${n.nama_pelanggan || ""}`.trim(),
          });
        } catch (e2) {
          toast.error(e2?.response?.data?.detail || "Gagal mengajukan izin hapus");
          return;
        }
        setPinDialog({ pin: "", submitting: false, error: "", retry: (pin) => doDeleteNota(n.id, pin) });
      } else {
        toast.error(e?.response?.data?.detail || "Gagal hapus");
      }
    }
  };

  const submitPinDialog = async () => {
    if (!pinDialog) return;
    setPinDialog(d => ({ ...d, submitting: true, error: "" }));
    try {
      await pinDialog.retry(pinDialog.pin);
      setPinDialog(null);
    } catch (e) {
      if (e?.response?.status === 403) {
        setPinDialog(d => ({ ...d, submitting: false, error: "PIN salah atau sudah kedaluwarsa — coba lagi." }));
      } else {
        toast.error(e?.response?.data?.detail || "Gagal menghapus");
        setPinDialog(d => ({ ...d, submitting: false }));
      }
    }
  };

  // Item helpers
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { nama_barang: "", harga: 0, kode: "HP", kategori: KATEGORI_DEFAULT, sku: "", imei: "", is_bonus: false, komponen: null, diskon_rp: 0 }] }));
  const rmItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, j) => j !== i) }));
  const updItem = (i, k, v) => setForm(f => {
    const arr = [...f.items];
    arr[i] = { ...arr[i], [k]: (k === "harga" || k === "diskon_rp") ? (parseFloat(v) || 0) : v };
    // Barang bonus tidak bisa berdiskon (server menolaknya) — kolomnya ikut
    // dinolkan saat dicentang, bukan ditinggal sebagai angka yang tak terlihat.
    if (k === "is_bonus" && v) arr[i].diskon_rp = 0;
    // Kategori ikut disarankan ulang HANYA sebagai cadangan teks bebas (baris
    // tanpa SKU) — baris ber-SKU tidak pernah lewat jalur ini, lihat
    // ItemNamaField.pilih() yang memakai kategori master produk apa adanya.
    if (k === "nama_barang") { arr[i].kode = deriveKode(v); arr[i].kategori = deriveKategoriTeksBebas(v); }
    return { ...f, items: arr };
  });
  // Tempel beberapa field item sekaligus — dipakai autocomplete produk supaya
  // nama_barang, sku, harga, dan kategori terisi dalam satu update.
  const patchItem = (i, patch) => setForm(f => {
    const arr = [...f.items];
    arr[i] = { ...arr[i], ...patch };
    return { ...f, items: arr };
  });

  // Terima baris hasil urai AI → TAMBAHKAN ke items form yang sudah ada.
  // qty dipecah jadi baris terpisah karena form nota tidak punya kolom qty —
  // satu baris = satu barang, sesuai perhitungan total & laporan upselling.
  const applyAiItems = (rows) => {
    if (!rows?.length) return;
    setForm(f => {
      const tambahan = [];
      rows.forEach(r => {
        const n = Math.max(1, parseInt(r.qty) || 1);
        for (let i = 0; i < n; i++) {
          tambahan.push({
            nama_barang: r.nama_barang || "",
            harga: parseFloat(r.harga) || 0,
            kode: r.kode || deriveKode(r.nama_barang),
            // Hasil AI selalu tanpa SKU (lihat komentar `sku` di bawah) — cadangan
            // teks bebas yang sama dipakai baris ketik manual, TIDAK menebak
            // Tab/Laptop/Wearable dari teks.
            kategori: r.kategori || deriveKategoriTeksBebas(r.nama_barang),
            sku: "", // hasil AI tidak pernah pra-tertaut katalog — kasir bisa pilih lewat autocomplete
            imei: "", // idem — IMEI baris cuma bisa dipilih setelah SKU tertaut
            is_bonus: false, // hasil AI tidak pernah menandai bonus — kasir yang menegaskan manual
          });
        }
      });
      // Baris kosong bawaan dibuang supaya hasil AI tidak nyelip di bawah baris kosong
      const terisi = f.items.filter(it => (it.nama_barang || "").trim());
      const next = { ...f, items: [...terisi, ...tambahan] };
      // Field IMEI nota tunggal — isi hanya kalau masih kosong, jangan menimpa ketikan kasir
      const imeiAi = rows.map(r => (r.imei || "").trim()).filter(Boolean);
      if (!(f.imei || "").trim() && imeiAi.length) next.imei = imeiAi.join(", ");
      return next;
    });
    toast.success(`${rows.length} baris hasil AI dimasukkan ke form`);
  };

  // Baris pembayaran diubah lewat BlokPembayaran (satu sumber dengan Input
  // Penjualan) — tidak ada lagi helper split tersendiri di halaman ini.
  const setBarisBayar = (rows) => setForm(f => ({ ...f, payments: rows }));

  // Trade-in item helpers (multi-unit)
  const addTradeItem = () => setForm(f => ({ ...f, trade_in_items: [...(f.trade_in_items || []),
    { nama: "", status: "", warna: "", bh: "", kelengkapan: "", imei: "", catatan: "", harga: 0 }] }));
  const rmTradeItem = (i) => setForm(f => ({ ...f, trade_in_items: (f.trade_in_items || []).filter((_, j) => j !== i) }));
  const updTradeItem = (i, k, v) => setForm(f => {
    const arr = [...(f.trade_in_items || [])];
    arr[i] = { ...arr[i], [k]: k === "harga" ? (parseFloat(v) || 0) : v };
    return { ...f, trade_in_items: arr };
  });
  // Auto-fill: nilai trade-in yang menutup sisa tagihan setelah DP dan uang
  // yang dibayar (split, atau ketikan Harga Terima untuk nota tanpa split).
  const autoFillTradeInBalance = () => {
    // Baris yang mengikuti sisa (baris utama nota tunai) tidak dihitung —
    // nominalnya menyesuaikan sendiri setelah trade-in terisi.
    const dibayar = (form.payments || []).filter(p => !p._ikutSisa)
      .reduce((a, p) => a + (Number(p.amount) || 0), 0);
    const gap = rincianLayar.tagihanMinDp - dibayar;
    if (gap <= 0) { toast.info("Tidak ada selisih untuk di-cover trade-in"); return; }
    const items = form.trade_in_items || [];
    if (items.length === 0) {
      setForm(f => ({ ...f, trade_in_items: [{ nama: "", harga: gap, imei: "" }] }));
    } else {
      const others = items.slice(0, -1);
      const otherSum = others.reduce((s, t) => s + (parseFloat(t.harga) || 0), 0);
      const lastNeeded = gap - otherSum;
      const arr = [...items];
      arr[arr.length - 1] = { ...arr[arr.length - 1], harga: Math.max(0, lastNeeded) };
      setForm(f => ({ ...f, trade_in_items: arr }));
    }
    toast.success(`Trade-in di-set jadi ${idr(gap)}`);
  };

  // Pengaman isian belum tersimpan (brief owner) — form Buat/Edit Nota adalah
  // form terpanjang di halaman ini (item + trade-in + IMEI). Hook dipanggil di
  // level komponen (bukan di dalam JSX kondisional `{modal && ...}`), jadi
  // `active` WAJIB diikat ke flag buka/tutup modalnya sendiri supaya status
  // "dirty" & konfirmasi ikut reset tiap modal dibuka ulang (lihat docstring
  // `active` di hooks/useModalGuard.jsx).
  const notaGuard = useModalGuard({
    onClose: () => setModal(false), onSubmit: submit, busy: saving, dangerous: true, active: modal,
  });
  // Dialog PPN (hand-rolled, di luar cakupan useModalGuard — lihat komentar
  // di titik render-nya) terbuka DI ATAS modal Nota ini. Daftarkan sebagai
  // lapisan lebih atas supaya Escape yang ditujukan untuk dialog PPN tidak
  // ikut memicu palang "ada isian belum disimpan" milik notaGuard di
  // baliknya (lihat "LAPISAN PALING ATAS YANG MENANG" di useModalGuard.jsx).
  useModalLayer(!!ppnConfirm);

  return (
    <div className="p-4 md:p-8 max-w-[1400px]" data-testid="input-nota-page">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">POS & Sales</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Input Nota Penjualan</h1>
          <p className="text-xs text-slate-500 mt-1">
            Sales input nota sementara → Kasir/Admin review & approve → auto-transfer ke Input Penjualan.
          </p>
          {/* Cetak faktur/struk DIHAPUS dari halaman ini (brief bos 21 Agu 2026
              #1) — nota yang sudah disetujui otomatis jadi dokumen penjualan,
              jadi pencetakannya dipusatkan di satu tempat. Link disembunyikan
              kalau pengguna sendiri tidak punya izin membuka halaman itu. */}
          {bolehLihatPenjualan && (
            <p className="text-xs text-slate-500 mt-1">
              Cetak faktur/struk dilakukan di{" "}
              <Link to="/sales" data-testid="nota-link-cetak-penjualan"
                className="text-emerald-700 hover:underline font-medium">Input Penjualan</Link>{" "}
              setelah nota disetujui.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canManageMethods && (
            <TombolKelolaMetode jumlah={paymentMethods.length} onClick={() => setMethodsModal(true)} />
          )}
          {/* Dibungkus lambda: openCreate kini menerima opsi, dan event klik
              tidak boleh terbaca sebagai opsi. */}
          <button onClick={() => openCreate()} disabled={memeriksaAntrian} data-testid="btn-new-nota"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm font-medium disabled:opacity-60">
            <Plus className="w-4 h-4" /> {memeriksaAntrian ? "Memeriksa antrian..." : "Buat Nota Baru"}
          </button>
        </div>
      </header>

      {wajibScan && (
        <div className="mb-4 flex flex-wrap items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
          data-testid="nota-antrian-wajib-spanduk">
          <Ticket className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span className="flex-1 min-w-0">
            Akun Anda wajib memindai nomor antrian pelanggan sebelum membuat nota.
          </span>
          <Link to="/antrian?tab=layani" data-testid="nota-antrian-wajib-link"
            className="font-medium text-amber-900 underline">
            Scan di Antrian
          </Link>
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3 md:gap-4 mb-5">
        <StatCard I={Clock} label="Menunggu" value={summary.pending || 0} tint="amber" />
        <StatCard I={CheckCircle2} label="Disetujui" value={summary.approved || 0} tint="emerald" />
        <StatCard I={XCircle} label="Ditolak" value={summary.rejected || 0} tint="rose" />
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 mb-4 flex flex-wrap items-center gap-2">
        <label className="text-xs font-medium text-slate-500">Status:</label>
        <select value={status} onChange={e => setStatus(e.target.value)} data-testid="filter-status"
          className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">Semua</option>
          <option value="pending">Menunggu Approval</option>
          <option value="approved">Disetujui</option>
          <option value="rejected">Ditolak</option>
        </select>
        {canApprove && (
          <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 ml-2">
            <input type="checkbox" checked={showMine} onChange={e => setShowMine(e.target.checked)}
              data-testid="filter-mine" className="w-3.5 h-3.5" />
            Hanya nota saya
          </label>
        )}
        {/* Periode, kategori, cabang, merek (brief owner 29 Agu poin 2-3) —
            semuanya disaring di SERVER. */}
        <span className="w-px h-6 bg-slate-200 mx-1" />
        <label className="text-xs font-medium text-slate-500">Periode:</label>
        <input type="date" value={fDari} onChange={e => setFDari(e.target.value)}
          data-testid="nota-filter-dari"
          className="h-9 px-2 rounded-lg border border-slate-200 text-sm" />
        <span className="text-xs text-slate-400">s/d</span>
        <input type="date" value={fSampai} onChange={e => setFSampai(e.target.value)}
          data-testid="nota-filter-sampai"
          className="h-9 px-2 rounded-lg border border-slate-200 text-sm" />
        <select value={fKategori} onChange={e => setFKategori(e.target.value)}
          data-testid="nota-filter-kategori"
          className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">Semua kategori</option>
          {KATEGORI_ITEM.map(k => <option key={k.v} value={k.v}>{k.l}</option>)}
        </select>
        <select value={fCabang} onChange={e => setFCabang(e.target.value)}
          data-testid="nota-filter-cabang"
          className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">Semua cabang</option>
          {opsiCabang.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        <select value={fBrand} onChange={e => setFBrand(e.target.value)}
          data-testid="nota-filter-brand"
          className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">Semua merek</option>
          {opsiBrand.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        {(fDari || fSampai || fKategori || fCabang || fBrand) && (
          <button onClick={() => { setFDari(""); setFSampai(""); setFKategori(""); setFCabang(""); setFBrand(""); }}
            data-testid="nota-filter-reset"
            className="h-9 px-3 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm">
            Reset
          </button>
        )}
        <span className="ml-auto text-[11px] text-slate-500">{items.length} nota ditampilkan</span>
      </div>

      {/* List — tabel penuh di layar besar, kartu bertumpuk di HP (staf sales
          paling sering membuka daftar nota ini lewat ponsel). */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <TabelDesktop>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <SortTh label="Tanggal" sortKeyName="tanggal" sortKey={notaSort.sortKey} sortDir={notaSort.sortDir} onSort={notaSort.toggle} testid="nota-sort-tanggal" />
              <SortTh label="Pelanggan" sortKeyName="nama_pelanggan" sortKey={notaSort.sortKey} sortDir={notaSort.sortDir} onSort={notaSort.toggle} testid="nota-sort-pelanggan" />
              <SortTh label="Sales" sortKeyName="nama_penjual" sortKey={notaSort.sortKey} sortDir={notaSort.sortDir} onSort={notaSort.toggle} testid="nota-sort-sales" />
              <SortTh label="Cabang" sortKeyName="branch" sortKey={notaSort.sortKey} sortDir={notaSort.sortDir} onSort={notaSort.toggle} testid="nota-sort-cabang" />
              <SortTh label="Total" sortKeyName="total_harga" sortKey={notaSort.sortKey} sortDir={notaSort.sortDir} onSort={notaSort.toggle} align="right" testid="nota-sort-total" />
              <th className="text-center py-3 px-4">Status</th>
              <th className="text-right py-3 px-4">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-sm text-slate-400">
                <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                Belum ada nota. Klik <b>Buat Nota Baru</b> untuk mulai.
              </td></tr>
            )}
            {sortRows(items, notaSort.sortKey, notaSort.sortDir).map(n => {
              const meta = STATUS_META[n.status] || STATUS_META.pending;
              const canEdit = n.status !== "approved" && (n.created_by_id === user?.id || canApprove);
              return (
                <tr key={n.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`nota-row-${n.id}`}>
                  <td className="py-3 px-4">
                    <p className="text-xs text-slate-900 font-medium">{n.tanggal}</p>
                    <p className="text-[10px] text-slate-500">{n.waktu}</p>
                  </td>
                  <td className="py-3 px-4">
                    <p className="text-sm font-medium text-slate-900">{n.nama_pelanggan}</p>
                    <p className="text-[10px] text-slate-500">{n.no_hp || "-"}</p>
                  </td>
                  <td className="py-3 px-4 text-xs text-slate-700">{n.nama_penjual}</td>
                  <td className="py-3 px-4 text-xs text-slate-600">{n.branch || "-"}</td>
                  <td className="py-3 px-4 text-right">
                    <p className="font-mono font-semibold text-slate-900">{idr(n.total_harga)}</p>
                    <p className="text-[10px] text-slate-500">{n.n_items} item{n.trade_in ? " · Trade-in" : ""}</p>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-${meta.color}-100 text-${meta.color}-800`}
                      data-testid={`nota-status-${n.id}`}>
                      <meta.icon className="w-3 h-3" /> {meta.label}
                    </span>
                    {n.status === "approved" && (
                      <p className="text-[10px] text-emerald-700 mt-0.5">by {n.approved_by_name || n.approved_by}</p>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right whitespace-nowrap">
                    <button onClick={() => setDetail(n)} title="Lihat detail" data-testid={`btn-detail-${n.id}`}
                      className="p-2 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-700">
                      <Eye className="w-4 h-4" />
                    </button>
                    {canEdit && (
                      <button onClick={() => openEdit(n)} title="Edit nota" data-testid={`btn-edit-${n.id}`}
                        className="p-2 rounded hover:bg-amber-50 text-slate-500 hover:text-amber-700">
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                    {canApprove && n.status === "pending" && (
                      <>
                        <button onClick={() => setKonfirmasiApprove(n)} title="Setujui" data-testid={`btn-approve-${n.id}`}
                          className="p-2 rounded hover:bg-emerald-50 text-slate-500 hover:text-emerald-700">
                          <CheckCircle2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => openReject(n)} title="Tolak" data-testid={`btn-reject-${n.id}`}
                          className="p-2 rounded hover:bg-rose-50 text-slate-500 hover:text-rose-700">
                          <XCircle className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    {(n.status !== "approved") && (n.created_by_id === user?.id || user?.role === "admin" || user?.role === "superadmin") && (
                      <button onClick={() => deleteNota(n)} title="Hapus" data-testid={`btn-del-${n.id}`}
                        className="p-2 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </TabelDesktop>

        <DaftarKartuMobile testid="nota-kartu">
          {items.length === 0 ? (
            <p className="text-center py-10 text-sm text-slate-400">
              <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              Belum ada nota. Klik <b>Buat Nota Baru</b> untuk mulai.
            </p>
          ) : sortRows(items, notaSort.sortKey, notaSort.sortDir).map(n => {
            const meta = STATUS_META[n.status] || STATUS_META.pending;
            const canEdit = n.status !== "approved" && (n.created_by_id === user?.id || canApprove);
            return (
              <KartuBaris key={n.id} testid={`nota-card-${n.id}`}>
                <KartuBarisHeader
                  kiri={<>
                    <p className="text-sm font-semibold text-slate-900">{n.nama_pelanggan}</p>
                    <p className="text-[11px] text-slate-500">{n.no_hp || "-"}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">{n.tanggal} · {n.waktu}</p>
                  </>}
                  kanan={<>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-${meta.color}-100 text-${meta.color}-800`}
                      data-testid={`nota-status-${n.id}-m`}>
                      <meta.icon className="w-3 h-3" /> {meta.label}
                    </span>
                    {n.status === "approved" && (
                      <p className="text-[10px] text-emerald-700 mt-0.5">by {n.approved_by_name || n.approved_by}</p>
                    )}
                  </>}
                />
                <KartuBarisMeta label="Sales" value={n.nama_penjual} />
                <KartuBarisMeta label="Cabang" value={n.branch || "-"} />
                <KartuBarisMeta label={`${n.n_items} item${n.trade_in ? " · Trade-in" : ""}`}
                  value={idr(n.total_harga)} tebal />
                <KartuBarisAksi>
                  <button onClick={() => setDetail(n)} title="Lihat detail" data-testid={`btn-detail-${n.id}-m`}
                    className="p-2 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-700">
                    <Eye className="w-4 h-4" />
                  </button>
                  {canEdit && (
                    <button onClick={() => openEdit(n)} title="Edit nota" data-testid={`btn-edit-${n.id}-m`}
                      className="p-2 rounded hover:bg-amber-50 text-slate-500 hover:text-amber-700">
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                  {canApprove && n.status === "pending" && (
                    <>
                      <button onClick={() => setKonfirmasiApprove(n)} title="Setujui" data-testid={`btn-approve-${n.id}-m`}
                        className="p-2 rounded hover:bg-emerald-50 text-slate-500 hover:text-emerald-700">
                        <CheckCircle2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => openReject(n)} title="Tolak" data-testid={`btn-reject-${n.id}-m`}
                        className="p-2 rounded hover:bg-rose-50 text-slate-500 hover:text-rose-700">
                        <XCircle className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  {(n.status !== "approved") && (n.created_by_id === user?.id || user?.role === "admin" || user?.role === "superadmin") && (
                    <button onClick={() => deleteNota(n)} title="Hapus" data-testid={`btn-del-${n.id}-m`}
                      className="p-2 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </KartuBarisAksi>
              </KartuBaris>
            );
          })}
        </DaftarKartuMobile>
      </div>

      {/* Modal: Create / Edit */}
      {modal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={notaGuard.requestClose}>
          <div ref={notaGuard.contentRef} className="bg-white rounded-2xl w-full max-w-3xl my-8 shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="nota-modal">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div>
                <h3 className="font-display font-semibold text-base">
                  {modalMode === "create" ? "Buat Nota Baru" : "Edit Nota"}
                </h3>
                <p className="text-[11px] text-emerald-100">
                  {modalMode === "create"
                    ? "Nota akan diteruskan ke kasir untuk direview."
                    : "Perubahan akan me-reset status jika sebelumnya ditolak."}
                </p>
              </div>
              <button onClick={notaGuard.requestClose} className="p-2 hover:bg-white/20 rounded-lg">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
              {form.kode_antrian && (
                <div className="flex flex-wrap items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900"
                  data-testid="nota-antrian-spanduk">
                  <Ticket className="w-4 h-4 flex-shrink-0" />
                  <span className="flex-1 min-w-0">
                    {nomorTiketForm ? <>Tiket antrian <b>No. {nomorTiketForm}</b> · </> : "Tiket antrian · "}
                    <span className="font-mono text-xs break-all">{form.kode_antrian}</span>
                  </span>
                  {modalMode === "create" && bolehBukaAntrian && (
                    <Link to="/antrian?tab=layani" data-testid="nota-antrian-kembali"
                      className="text-xs font-medium text-emerald-800 underline">
                      Kembali ke Antrian
                    </Link>
                  )}
                </div>
              )}
              {/* Basic info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Tanggal">
                  <DatePicker value={form.tanggal} onChange={v => setForm(f => ({ ...f, tanggal: v }))}
                    data-testid="input-tanggal" />
                </Field>
                <Field label={<span className="inline-flex items-center gap-1">Waktu <span className="text-[9px] px-1.5 py-0 rounded bg-emerald-100 text-emerald-800 font-bold">LIVE</span></span>}>
                  <input type="time" value={form.waktu} readOnly disabled
                    data-testid="input-waktu"
                    className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-slate-50 text-slate-600 cursor-not-allowed" />
                </Field>
                <Field label="Cabang *">
                  <select value={form.branch} onChange={e => setForm(f => ({ ...f, branch: e.target.value }))}
                    data-testid="input-cabang"
                    className={`w-full h-9 px-3 rounded-lg border text-sm bg-white ${galatCabang ? "border-rose-400" : "border-slate-200"}`}>
                    {BRANCHES.map(b => <option key={b} value={b}>{b || "— pilih —"}</option>)}
                    {/* Nota lama bisa menyimpan ejaan cabang yang tidak ada di daftar.
                        Tetap ditampilkan (dan dikirim apa adanya) supaya membuka lalu
                        menyimpan nota itu tidak diam-diam mengganti cabangnya. */}
                    {form.branch && !BRANCHES.includes(form.branch) && (
                      <option value={form.branch}>{form.branch} (tidak terdaftar)</option>
                    )}
                  </select>
                  {galatCabang && (
                    <p className="text-[11px] text-rose-700 mt-1" data-testid="input-cabang-galat">{galatCabang}</p>
                  )}
                </Field>
              </div>

              {/* N1 (brief owner 22 Sep 2026): Jenis Pelanggan di ATAS Nama.
                  Jenis menentukan apakah nama DIPILIH dari Data Pelanggan
                  (B2B/USER) atau diketik bebas, jadi harus diputuskan lebih dulu.
                  Nama B2B/USER bertempo WAJIB dari kartu pelanggan (membalik
                  keputusan G2 15 Sep "saran, bukan paksaan") — jalan keluarnya
                  tombol "+ Pelanggan baru" di kolom nama. */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="blok-identitas-pelanggan">
                <Field label="Jenis Pelanggan">
                  <select value={form.jenis_pelanggan || "umum"} data-testid="input-jenis-pelanggan"
                    onChange={e => setForm(f => ({
                      ...f, jenis_pelanggan: e.target.value,
                      // Ganti ke umum = tempo & PIC dibersihkan, kalau tidak
                      // nilainya tertinggal dan dikirim diam-diam.
                      tempo_pembayaran: e.target.value === "umum" ? "" : f.tempo_pembayaran,
                      pic_approval_piutang: e.target.value === "user" ? f.pic_approval_piutang : "",
                      // Tempo ikut lepas → baris utama kembali mengikuti sisa (N3).
                      payments: e.target.value === "umum" && f.tempo_pembayaran
                        ? aturBarisUtamaTempo(f.payments, false) : f.payments,
                    }))}
                    className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                    <option value="umum">Pelanggan Umum</option>
                    <option value="b2b">B2B</option>
                    <option value="user">USER</option>
                  </select>
                </Field>
                {(form.jenis_pelanggan || "umum") !== "umum" && (
                  <Field label="Tempo Pembayaran">
                    <select value={form.tempo_pembayaran || ""} data-testid="input-tempo"
                      onChange={e => setForm(f => ({
                        ...f, tempo_pembayaran: e.target.value,
                        // Baris utama nota BERTEMPO = uang muka opsional (0), bukan
                        // sisa tagihan — bawaan tunai yang terbawa ke nota bertempo
                        // membuatnya tercatat lunas dan piutangnya tidak lahir (N3).
                        payments: !!e.target.value !== !!f.tempo_pembayaran
                          ? aturBarisUtamaTempo(f.payments, !!e.target.value) : f.payments,
                      }))}
                      className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                      <option value="">Bayar di tempat (tanpa tempo)</option>
                      <option value="net_3">Net 3</option>
                      <option value="net_7">Net 7</option>
                      <option value="net_10">Net 10</option>
                      <option value="net_30">Net 30</option>
                      <option value="net_90">Net 90</option>
                      <option value="deposit">Deposit</option>
                    </select>
                    {form.tempo_pembayaran && (
                      <p className="text-[11px] text-amber-700 mt-1" data-testid="input-tempo-hint">
                        Nota ini jadi <b>piutang</b>. Sisa yang belum dibayar masuk ke
                        Piutang Usaha, dan Finance harus memverifikasinya dulu sebelum
                        bisa ditagih.
                      </p>
                    )}
                  </Field>
                )}
                {(form.jenis_pelanggan || "umum") === "umum" && <div className="hidden md:block" />}
                {form.jenis_pelanggan === "user" && (
                  <div className="md:col-span-2">
                    <Field label="PIC Persetujuan Piutang *">
                      <input value={form.pic_approval_piutang || ""} data-testid="input-pic-piutang"
                        onChange={e => setForm(f => ({ ...f, pic_approval_piutang: e.target.value }))}
                        placeholder="Siapa yang menyetujui piutang ini"
                        className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
                    </Field>
                  </div>
                )}
                {/* <div>, bukan <Field> (<label>): kolom ini memuat tombol
                    "Ganti" & "+ Pelanggan baru" — di dalam <label>, klik pada
                    teks mana pun diteruskan browser ke tombol pertama. */}
                <div className="block">
                  <span className="text-xs font-medium text-slate-600">Nama Pelanggan *</span>
                  <div className="mt-1">
                    <PilihPelangganNota
                      jenis={form.jenis_pelanggan}
                      nama={form.nama_pelanggan}
                      onNama={v => setForm(f => ({
                        ...f, nama_pelanggan: v,
                        // Mengetik ulang nama melepas kartu yang tadi dipilih.
                        _kartu: f._kartu && kunciNama(f._kartu.nama) === kunciNama(v) ? f._kartu : null,
                      }))}
                      onPilih={pilihKartu}
                      kartu={form._kartu}
                      onLepas={() => setForm(f => ({ ...f, _kartu: null, nama_pelanggan: "" }))}
                      noHp={form.no_hp}
                      aturan={aturanIdentitas}
                      perluKartu={perluKartu}
                      testid="input-pelanggan"
                    />
                    {/* Kredit retur milik pelanggan ini (owner 26 Agu 2026; nama +
                        No HP sejak 22 Sep). Tanpa ditampilkan di sini, kreditnya
                        cuma catatan di database: kasir tidak punya cara tahu
                        pelanggan punya kredit, dan pelanggan yang lupa akan
                        kehilangan uangnya. */}
                    <PetunjukKreditRetur info={kreditInfo} />
                  </div>
                </div>
                <Field label="No HP *">
                  <input value={form.no_hp} onChange={e => setForm(f => ({ ...f, no_hp: e.target.value }))}
                    data-testid="input-no-hp" inputMode="tel"
                    className={`w-full h-9 px-3 rounded-lg border text-sm ${galatHpTampil ? "border-rose-400" : "border-slate-200"}`}
                    placeholder="0812 3456 7890" />
                  {galatHpTampil && (
                    <p className="text-[11px] text-rose-700 mt-1" data-testid="input-no-hp-galat">{galatHpTampil}</p>
                  )}
                  {identitasAwal && !identitasAwal.no_hp && !hpBerubah && (
                    <p className="text-[11px] text-slate-500 mt-1" data-testid="input-no-hp-lama">
                      Nota lama tanpa No HP — boleh disimpan apa adanya; kalau diisi, harus nomor yang sah.
                    </p>
                  )}
                </Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Brief owner 22 Sep 2026 (D2): kolom Diskon nota & Biaya Admin
                    DIHAPUS dari form — diskon kini diisi per barang (kolom
                    Diskon di baris "Barang Dibeli"). Draf LAMA yang sudah
                    memakainya tetap dihitung & dijurnal seperti dulu; di sini
                    tampil baca-saja dan hanya bisa DIHAPUS, tidak ditambah. */}
                {form._diskon_lama?.ada && (
                  <div className="md:col-span-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs"
                    data-testid="nota-diskon-lama">
                    {form._diskon_lama.dihapus ? (
                      <p className="text-amber-900">
                        Diskon nota / biaya admin lama akan <b>dihapus</b> saat disimpan.{" "}
                        <button type="button" className="underline" data-testid="btn-batal-hapus-diskon-lama"
                          onClick={() => setForm(f => ({ ...f, _diskon_lama: { ...f._diskon_lama, dihapus: false } }))}>
                          Batalkan
                        </button>
                      </p>
                    ) : (
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-amber-900">
                          Nota lama:
                          {form._diskon_lama.diskon > 0 && (
                            <> diskon nota <b className="font-mono">{idr(form._diskon_lama.diskon)}</b>
                              {form._diskon_lama.pic ? ` (PIC ${form._diskon_lama.pic})` : ""}</>
                          )}
                          {form._diskon_lama.mode && form._diskon_lama.nilai > 0 && (
                            <> · biaya admin <b className="font-mono">
                              {form._diskon_lama.mode === "persen" ? `${form._diskon_lama.nilai}%` : idr(form._diskon_lama.nilai)}
                            </b></>
                          )}
                          {" "}— tetap dihitung. Kolomnya sudah tidak ada di form baru.
                        </p>
                        <button type="button" data-testid="btn-hapus-diskon-lama"
                          onClick={() => setForm(f => ({ ...f, _diskon_lama: { ...f._diskon_lama, dihapus: true } }))}
                          className="h-7 px-2 rounded bg-white border border-amber-300 text-amber-900 hover:bg-amber-100">
                          Hapus diskon lama
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {diskonBarisForm > 0 && (
                  <Field label="PIC Approval Diskon *">
                    <input value={form.diskon_pic_approval || ""} data-testid="input-diskon-pic"
                      onChange={e => setForm(f => ({ ...f, diskon_pic_approval: e.target.value }))}
                      placeholder="Siapa yang menyetujui diskon barang"
                      className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
                    <p className="text-[11px] text-slate-500 mt-1">
                      Diskon dicatat sebagai potongan tersendiri di pembukuan (akun 4190), bukan
                      dengan menurunkan harga barang — supaya &quot;bulan ini kita memberi
                      diskon berapa&quot; bisa dijawab.
                    </p>
                  </Field>
                )}
                {/* Kolom IMEI manual tingkat nota DIHAPUS (brief owner 29 Agu,
                    Input Nota poin 1): "sudah tidak digunakan lagi, karena sudah
                    ada imei otomatis yang terkoneksi ke inventory". IMEI kini
                    HANYA dipilih per BARIS barang dari unit yang benar-benar ada
                    di gudang — mengetiknya bebas di sini membuat nota mengklaim
                    unit yang tidak pernah terdaftar.

                    Medannya sendiri tetap ada di model & dokumen lama supaya
                    nota yang sudah terlanjur mengisinya tidak berubah. */}
                <Field label={
                  <div className="flex items-center justify-between">
                    <span>Nama Sales *</span>
                    <label className="inline-flex items-center gap-1 text-[10px] text-slate-500 font-normal cursor-pointer">
                      <input type="checkbox" checked={penjualScope === "all"} onChange={e => setPenjualScope(e.target.checked ? "all" : "mine")}
                        data-testid="penjual-scope-all" className="w-3 h-3" />
                      Pilih dari semua karyawan
                    </label>
                  </div>
                }>
                  <select value={form.nama_penjual} onChange={e => setForm(f => ({ ...f, nama_penjual: e.target.value }))}
                    data-testid="input-sales"
                    className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                    <option value="">— pilih karyawan —</option>
                    {availableEmployees.map(e => (
                      <option key={e.id} value={e.nama}>
                        {e.nama}{e.branch ? ` · ${e.branch}` : ""}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              {/* Sumber Info / atribusi marketing (brief bos 11 Agu 2026) —
                  lihat components/SourceInfoField.jsx untuk alasan lengkap. */}
              <SourceInfoField
                sourceInfo={form.source_info}
                sourceRefId={form.source_ref_id}
                onChange={(v, refId) => setForm(f => ({ ...f, source_info: v, source_ref_id: refId }))}
                cfg={sourceInfoCfg}
                testidPrefix="nota-source-info"
              />

              {/* Items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-slate-700">Barang Dibeli</p>
                  <div className="flex items-center gap-2">
                    {/* Gerbang fitur AI (brief owner 30 Agu 2026) — kunci
                        `ai_assistant`, sama dengan backend /ai-produk/parse.
                        Input manual di bawah tidak berubah sama sekali. */}
                    {bolehPakaiAI(user, can) && (
                    <button onClick={() => setAiPanel(true)} data-testid="btn-input-via-ai"
                      title="Tempel teks bebas — AI mengubahnya jadi baris barang"
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-slate-800 hover:bg-slate-900 text-white font-medium">
                      <Sparkles className="w-3 h-3" /> Input via AI
                    </button>
                    )}
                    <button onClick={addItem} data-testid="btn-add-item"
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-emerald-100 text-emerald-800 hover:bg-emerald-200 font-medium">
                      <Plus className="w-3 h-3" /> Tambah Barang
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  {form.items.map((it, i) => {
                    // Paket/bundling (poin 8, brief bos 23 Agu 2026) — sama
                    // pola deteksi dengan Sales.jsx (lewat KEBERADAAN resep
                    // aktif untuk SKU baris ini, bukan `it.komponen`).
                    const resepBaris = it.sku ? resepBundlingMap[it.sku.trim().toUpperCase()] : null;
                    return (
                    <div key={i} className="grid grid-cols-[1fr,110px,140px,auto] gap-2 items-start">
                      <div>
                        <ItemNamaField index={i} item={it} bolehLookup={bolehLookupProduk}
                          resepBundlingMap={resepBundlingMap}
                          onPatch={(patch) => patchItem(i, patch)} />
                        {/* IMEI baris ini HANYA muncul untuk barang yang sudah tertaut SKU
                            (brief bos poin 4) — barang ketik bebas tanpa SKU tidak pernah
                            dipaksa mengisi IMEI, lihat docstring ItemImeiField. Baris PAKET
                            (poin 8) tidak pernah punya unit fisik untuk SKU paketnya sendiri
                            — IMEI-nya per KOMPONEN lewat BundleKomponenPicker di bawah. */}
                        {bolehLookupUnit && it.sku && !resepBaris && (
                          <ItemImeiField index={i} item={it} bolehLookup={bolehLookupUnit}
                            imeiTerpakaiLain={new Set(form.items
                              .filter((_, j) => j !== i)
                              .map(x => (x.imei || "").trim())
                              .filter(Boolean))}
                            onPatch={(patch) => patchItem(i, patch)} />
                        )}
                        {(resepBaris || (Array.isArray(it.komponen) && it.komponen.length > 0)) && (
                          <BundleKomponenPicker index={i} resep={resepBaris} komponen={it.komponen}
                            onChange={(next) => patchItem(i, { komponen: next })}
                            bolehLookupUnit={bolehLookupUnit}
                            imeiTerpakaiLain={kumpulkanSemuaImeiForm(form.items)}
                            produkInfoCache={produkInfoCache} ensureProdukInfo={ensureProdukInfo} />
                        )}
                      </div>
                      <div className="space-y-1">
                        <InputRupiah value={it.harga} onChange={v => updItem(i, "harga", v)}
                          placeholder="Harga" testid={`item-price-${i}`}
                          className="h-9 w-full px-2 rounded border border-slate-200 text-xs text-right font-mono" />
                        {/* Diskon per barang (brief owner 22 Sep 2026, D3) — Rupiah
                            untuk SELURUH baris. Harga tetap bruto; potongannya
                            dijurnal terpisah ke 4190. Baris bonus tidak berdiskon. */}
                        {!it.is_bonus && (
                          <InputRupiah value={it.diskon_rp || 0} onChange={v => updItem(i, "diskon_rp", v)}
                            placeholder="Diskon" testid={`item-diskon-${i}`}
                            className={`h-8 w-full px-2 rounded border text-[11px] text-right font-mono ${
                              diskonBaris(it) > 0 ? "border-rose-300 text-rose-700" : "border-slate-200"}`} />
                        )}
                        {/* Backend menolak harga Rp0 kecuali ditandai bonus — checkbox ini
                            BUKAN cara "mengakali" harga, hanya untuk barang yang memang
                            diberikan gratis (bonus/hadiah/garansi pembelian). */}
                        <label className="flex items-center gap-1.5 text-[10px] text-slate-500 cursor-pointer"
                          title="Centang kalau barang ini memang diberikan GRATIS (bonus/hadiah/garansi) — bukan cara mengosongkan harga jual.">
                          <input type="checkbox" checked={!!it.is_bonus}
                            onChange={e => updItem(i, "is_bonus", e.target.checked)}
                            data-testid={`item-bonus-${i}`} className="w-3 h-3 accent-emerald-600" />
                          Bonus/Gratis
                        </label>
                      </div>
                      {/* Baris bertaut SKU: kategori diambil APA ADANYA dari master
                          produk (lihat ItemNamaField.pilih()) — dikunci, bukan
                          dropdown, supaya sales tidak bisa diam-diam menimpanya
                          dengan kategori yang salah (akun neraca ikut salah kalau
                          itu terjadi, lihat memory/MIGRASI-KATEGORI-23AGU.md).
                          Lepas SKU dulu (tombol × di badge SKU) kalau memang perlu
                          diketik bebas. Baris TANPA SKU tetap dropdown biasa, karena
                          tidak ada sumber kebenaran lain selain pilihan sales. */}
                      {it.sku ? (
                        <div data-testid={`item-kategori-${i}`}
                          title="Kategori mengikuti master produk — lepas tautan SKU untuk mengubah manual"
                          className="h-9 px-2 rounded border border-slate-200 bg-slate-50 text-xs text-slate-600 flex items-center">
                          {labelKategori(it.kategori)}
                        </div>
                      ) : (
                        <select value={normalKategori(it.kategori) || KATEGORI_DEFAULT} onChange={e => updItem(i, "kategori", e.target.value)}
                          data-testid={`item-kategori-${i}`}
                          className="h-9 px-2 rounded border border-slate-200 text-xs bg-white">
                          {KATEGORI_ITEM.map(k => <option key={k.v} value={k.v}>{k.l}</option>)}
                        </select>
                      )}
                      <button onClick={() => rmItem(i)} disabled={form.items.length <= 1}
                        className="p-1.5 hover:bg-rose-50 rounded text-slate-400 hover:text-rose-600 disabled:opacity-30">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    );
                  })}
                </div>
                <p className="text-right text-sm font-semibold text-emerald-700 mt-2">
                  Total Barang: <span className="font-mono text-base">{idr(totalHarga)}</span>
                </p>
                {/* N2: nilai setelah diskon per barang — dari rumus bersama
                    (lib/rincianBayar.js), bukan pengurangan yang diketik ulang. */}
                {diskonBarisForm > 0 && (
                  <p className="text-right text-xs text-slate-600 mt-0.5" data-testid="nota-total-setelah-diskon">
                    − Diskon barang <span className="font-mono text-rose-700">{idr(diskonBarisForm)}</span>
                    {" "}= <span className="font-mono font-semibold text-slate-900">
                      {idr(rincianBayar({ total: totalHarga, diskon: diskonBarisForm }).nilaiTagihan)}
                    </span>
                  </p>
                )}
                {/* PPN (keputusan owner 18 Agu 2026): CV ZOA berstatus PKP,
                    tarif 11%, harga barang di atas BELUM termasuk PPN — dicentang
                    di sini untuk menambahkan PPN sebagai kolom terpisah. */}
                <div className="mt-2 pt-2 border-t border-slate-100">
                  <label className="inline-flex items-center gap-2 text-xs font-medium text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={!!form.kena_ppn}
                      onChange={e => setForm(f => ({ ...f, kena_ppn: e.target.checked }))}
                      data-testid="input-kena-ppn" className="w-3.5 h-3.5 accent-emerald-600" />
                    Kena PPN 11% (harga di atas belum termasuk PPN)
                  </label>
                  {form.kena_ppn && (
                    <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-xs space-y-0.5"
                      data-testid="ringkasan-ppn">
                      <div className="flex justify-between text-slate-600">
                        <span>{diskonForm > 0 ? "DPP (sesudah diskon, sebelum PPN)" : "DPP (sebelum PPN)"}</span>
                        <span className="font-mono">{idr(dpp)}</span>
                      </div>
                      <div className="flex justify-between text-slate-600">
                        <span>PPN ({PPN_PERSEN_PREVIEW}%)</span>
                        <span className="font-mono">{idr(ppnPreview)}</span>
                      </div>
                      <div className="flex justify-between font-semibold text-emerald-700 pt-0.5 border-t border-emerald-200">
                        <span>Total (DPP + PPN)</span>
                        <span className="font-mono">{idr(totalDenganPpn)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Blok pembayaran (batch N3, brief 22 Sep D4–D8): baris metode
                  + keterangan, lalu trade-in & DP DI BAWAH metode pembayaran
                  ("Trade in ditempatkan dibawah metode pembayaran", "tempatkan dan
                  tampilkan downpayment tersebut dibawah metode pembayaran"). */}
              <BlokPembayaran rows={form.payments} onChange={setBarisBayar} groups={paymentGroups}
                rincian={rincianLayar} tempo={bertempo} testidPrefix="nota-bayar">
                {pakaiKreditRetur && (
                  <p className="text-[11px] text-violet-800 bg-violet-50 border border-violet-200 rounded px-2 py-1"
                     data-testid="hint-kredit-retur-baris">
                    Baris Kredit Retur dipotong dari kredit retur pelanggan saat nota disetujui
                    (saldo {idr(kreditPelanggan)}).
                  </p>
                )}
                <Field label={
                  <div className="inline-flex items-center gap-1.5">
                    <Repeat className="w-3 h-3" /> Trade-in?
                  </div>
                }>
                  <div className="flex items-center gap-3 h-9">
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={form.trade_in}
                        onChange={e => setForm(f => ({ ...f, trade_in: e.target.checked }))}
                        data-testid="input-tradein" /> Ya, ada unit trade-in
                    </label>
                  </div>
                </Field>
                {/* Trade-in multi-unit list */}
                {form.trade_in && (
                  <div className="border border-violet-200 rounded-lg p-3 bg-violet-50/40">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold uppercase tracking-wider text-violet-700 inline-flex items-center gap-1.5">
                        <Repeat className="w-3.5 h-3.5" /> Unit Trade-in
                      </p>
                      <div className="flex items-center gap-2">
                        <button onClick={autoFillTradeInBalance} type="button" data-testid="btn-tradein-autofill"
                          title="Set harga trade-in otomatis = sisa tagihan yang belum tertutup pembayaran"
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-violet-600 hover:bg-violet-700 text-white font-medium">
                          Auto-Fill Selisih
                        </button>
                        <button onClick={addTradeItem} type="button" data-testid="btn-add-tradein"
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-violet-100 text-violet-800 hover:bg-violet-200 font-medium">
                          <Plus className="w-3 h-3" /> Tambah Unit
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {(form.trade_in_items || []).map((t, i) => (
                        <div key={i} className="rounded-lg border border-violet-100 bg-violet-50/40 p-2 space-y-2">
                         <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                          <input value={t.nama}
                            onChange={e => updTradeItem(i, "nama", e.target.value)}
                            placeholder="Nama barang *"
                            data-testid={`tradein-name-${i}`}
                            className="h-9 px-2 rounded border border-slate-200 text-xs bg-white" />
                          <select value={t.status || ""} data-testid={`tradein-status-${i}`}
                            onChange={e => updTradeItem(i, "status", e.target.value)}
                            className="h-9 px-2 rounded border border-slate-200 text-xs bg-white">
                            <option value="">Status *</option>
                            {TI_STATUS.map(v => <option key={v} value={v}>{v}</option>)}
                          </select>
                          <input value={t.warna || ""} data-testid={`tradein-warna-${i}`}
                            onChange={e => updTradeItem(i, "warna", e.target.value)}
                            placeholder="Warna *"
                            className="h-9 px-2 rounded border border-slate-200 text-xs bg-white" />
                          <input value={t.bh || ""} data-testid={`tradein-bh-${i}`}
                            onChange={e => updTradeItem(i, "bh", e.target.value)}
                            placeholder="BH (mis. 85%)"
                            title="Battery Health — opsional"
                            className="h-9 px-2 rounded border border-slate-200 text-xs bg-white" />
                          <select value={t.kelengkapan || ""} data-testid={`tradein-kelengkapan-${i}`}
                            onChange={e => updTradeItem(i, "kelengkapan", e.target.value)}
                            className="h-9 px-2 rounded border border-slate-200 text-xs bg-white">
                            <option value="">Kelengkapan *</option>
                            {TI_KELENGKAPAN.map(v => <option key={v} value={v}>{v}</option>)}
                          </select>
                         </div>
                         {/* Output tergabung diperlihatkan SEBELUM disimpan (poin 5)
                             — kasir bisa melihat nama yang akan benar-benar tercatat,
                             bukan menebaknya dari lima kolom terpisah. */}
                         {rakitNamaTradeIn(t) && (
                           <p className="text-[11px] font-mono text-violet-800" data-testid={`tradein-preview-${i}`}>
                             → {rakitNamaTradeIn(t)}
                           </p>
                         )}
                         <div className="grid grid-cols-[1fr,170px,140px,auto] gap-2 items-center">
                          <input value={t.catatan || ""} data-testid={`tradein-catatan-${i}`}
                            onChange={e => updTradeItem(i, "catatan", e.target.value)}
                            placeholder="Catatan (opsional)"
                            className="h-9 px-2 rounded border border-slate-200 text-xs bg-white" />
                          <input value={t.imei || ""}
                            onChange={e => updTradeItem(i, "imei", e.target.value)}
                            placeholder="IMEI / SN *"
                            inputMode="numeric"
                            maxLength={30}
                            title="IMEI/SN unit trade-in — bentuk bebas. Boleh dikosongkan."
                            data-testid={`tradein-imei-${i}`}
                            className="h-9 px-2 rounded border border-slate-200 text-xs font-mono bg-white" />
                          <InputRupiah value={t.harga || ""}
                            onChange={v => updTradeItem(i, "harga", v)}
                            placeholder="Nilai Rp"
                            testid={`tradein-price-${i}`}
                            className="h-9 px-2 rounded border border-slate-200 text-xs text-right font-mono bg-white" />
                          <button onClick={() => rmTradeItem(i)} type="button"
                            disabled={(form.trade_in_items || []).length <= 1}
                            className="p-1.5 hover:bg-rose-50 rounded text-slate-400 hover:text-rose-600 disabled:opacity-30">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                         </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-right text-xs text-violet-700 mt-2">
                      Total Trade-in: <span className="font-mono font-bold text-base">{idr(tradeInTotal)}</span>
                    </p>
                    {/* D8: nilai trade-in bukan uang kas — ia masuk akun Penampung
                        Trade-in cabang (modal usaha) dan dipakai melunasi PO Barang
                        Trade-in saat gudang menerima unitnya (mekanisme H1). */}
                    <p className="text-[11px] text-violet-800 mt-1" data-testid="nota-tradein-penampung">
                      Nilai trade-in dicatat di Penampung Trade-in cabang ini (modal usaha, bukan kas),
                      lalu dipakai melunasi saat gudang menerima barangnya lewat PO Barang Trade-in.
                    </p>
                  </div>
                )}
                  {/* Kolom DP TIDAK disembunyikan saat daftarnya kosong. Versi lama
                      menyembunyikannya, sehingga cacat pencarian nomor HP
                      (`re.escape` pada pencocokan persis) tampak sebagai "fitur
                      DP tidak ada" — gagal tanpa satu pun pesan, selama dua
                      minggu tanpa ada yang tahu. */}
                  {dpDicari && (
                    <Field label="Down Payment (DP)">
                      {dpTersedia.length === 0 ? (
                        <p className="text-xs text-slate-500 py-2" data-testid="input-dp-kosong">
                          Tidak ada DP tersisa atas nama / nomor HP pelanggan ini.
                        </p>
                      ) : (
                        <select value={form.dp_id || ""} data-testid="input-dp"
                          onChange={e => setForm(f => ({ ...f, dp_id: e.target.value, dp_nominal: "" }))}
                          className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                          <option value="">— tanpa DP —</option>
                          {dpTersedia.map(d => (
                            <option key={d.id} value={d.id}>
                              {d.pelanggan_nama} · {d.code} · {d.tanggal} · sisa {idr(d.sisa)}
                            </option>
                          ))}
                        </select>
                      )}
                      {dpTerpilih && (
                        <>
                          <input type="number" inputMode="numeric" value={form.dp_nominal || ""}
                            data-testid="input-dp-nominal"
                            onChange={e => setForm(f => ({ ...f, dp_nominal: e.target.value }))}
                            placeholder="kosongkan = pakai seluruh sisa"
                            className="mt-1 w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
                          <div className="mt-1 rounded-lg bg-emerald-50 border border-emerald-100 px-2.5 py-2 text-[11px] text-emerald-900 space-y-0.5"
                               data-testid="input-dp-hint">
                            <div className="flex justify-between gap-3">
                              <span>Saldo DP</span>
                              <b data-testid="input-dp-saldo">{idr(dpSaldo)}</b>
                            </div>
                            <div className="flex justify-between gap-3">
                              <span>Nominal digunakan</span>
                              <b data-testid="input-dp-dipakai">{idr(dpDipakai)}</b>
                            </div>
                            <div className="flex justify-between gap-3 border-t border-emerald-200 pt-0.5">
                              <span>Sisa DP pelanggan</span>
                              <b data-testid="input-dp-sisa">{idr(dpSisaSetelah)}</b>
                            </div>
                            <div className="flex justify-between gap-3 border-t border-emerald-200 pt-0.5">
                              <span>Sisa yang harus dibayar</span>
                              {/* = diterima di kasir, rumus server (termasuk admin
                                  & trade-in) — dulu angka ini mengabaikan keduanya. */}
                              <b data-testid="input-dp-sisa-bayar">
                                {idr(Math.max(0, rincianLayar.diTempat))}
                              </b>
                            </div>
                          </div>
                        </>
                      )}
                    </Field>
                  )}
                  {/* DOWNGRADE (poin 6-7): trade-in lebih tinggi dari belanja.
                      Selisihnya bukan kembalian di laci — ia dibayarkan lewat
                      transfer, jadi rekeningnya WAJIB diketahui sebelum notanya
                      bisa disimpan. */}
                  {selisihDowngrade > 0 && (
                    <>
                      <div className="col-span-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
                           data-testid="input-downgrade-notice">
                        <p className="text-xs font-semibold text-amber-900">
                          Transaksi DOWNGRADE — perusahaan membayar {idr(selisihDowngrade)} ke pelanggan
                        </p>
                        <p className="text-[11px] text-amber-800 mt-0.5">
                          Nilai unit trade-in lebih tinggi daripada total belanjanya. Isi rekening
                          tujuan pengembalian dana di bawah.
                        </p>
                      </div>
                      <Field label="Nama Bank *">
                        <input value={form.refund_nama_bank || ""} data-testid="input-refund-bank"
                          onChange={e => setForm(f => ({ ...f, refund_nama_bank: e.target.value }))}
                          placeholder="mis. BCA"
                          className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
                      </Field>
                      <Field label="Nomor Rekening *">
                        <input value={form.refund_no_rekening || ""} data-testid="input-refund-rek"
                          onChange={e => setForm(f => ({ ...f, refund_no_rekening: e.target.value }))}
                          className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm font-mono" />
                      </Field>
                      <Field label="Atas Nama *">
                        <input value={form.refund_atas_nama || ""} data-testid="input-refund-nama"
                          onChange={e => setForm(f => ({ ...f, refund_atas_nama: e.target.value }))}
                          className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
                      </Field>
                    </>
                  )}
              </BlokPembayaran>
              {galatBayar && (
                <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2"
                   data-testid="nota-bayar-galat">{galatBayar}</p>
              )}

              {/* Total Pembayaran (D6: "hanya bisa di isi role kasir ... penempatannya
                  di atas catatan"). Kasir yang membuat/menyunting draf boleh mengisinya
                  di sini; kalau kosong, ditanyakan lagi di jendela approve. Kosong dan
                  0 BERBEDA (`bolehKosong`): 0 sah untuk nota bertempo tanpa uang muka. */}
              <Field label="Total Pembayaran">
                {canApprove ? (
                  <>
                    <InputRupiah value={form.total_pembayaran_kasir} bolehKosong
                      onChange={v => setForm(f => ({ ...f, total_pembayaran_kasir: v === null ? "" : v }))}
                      testid="input-total-pembayaran" placeholder="Uang yang Anda terima (boleh diisi saat approve)"
                      className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm text-right font-mono" />
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Jumlah SEMUA pembayaran lewat metode pembayaran (tunai + transfer + kartu/EDC +
                      leasing/cicilan), di luar trade-in, DP, dan Kredit Retur. Ditolak kalau tidak sama
                      dengan angka sistem.
                    </p>
                  </>
                ) : (
                  <p className="text-xs text-slate-500 py-2" data-testid="input-total-pembayaran-kasir">
                    Diisi kasir saat menyetujui nota.
                  </p>
                )}
              </Field>

              <Field label="Catatan">
                <textarea value={form.catatan} onChange={e => setForm(f => ({ ...f, catatan: e.target.value }))}
                  rows={2} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
              </Field>
            </div>

            {/* Paket/bundling (poin 8-9, brief bos 23 Agu 2026) — alasan tombol
                Kirim/Simpan tertahan, aturan #3 CLAUDE.md. */}
            {bundleAlasanTertahan && (
              <p className="mx-4 mb-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
                data-testid="nota-bundle-alasan-tertahan">
                {bundleAlasanTertahan}
              </p>
            )}
            {scanInline && modalMode === "create" && (
              <div className="mx-4 mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 space-y-2"
                data-testid="nota-antrian-scan-inline">
                {pesanTolakAntrian && (
                  <p className="text-xs text-rose-700">{pesanTolakAntrian}</p>
                )}
                <p className="text-xs text-amber-900">
                  Pindai tiket antrian pelanggan ini, lalu tekan <b>Kirim ke Kasir</b> lagi. Isian nota tidak hilang.
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <BarcodeScanner
                    onDetected={(kode) => panggilTiketInline(kode)}
                    testId="nota-antrian-scan-btn"
                    title="Scan barcode tiket antrian"
                  />
                  <input
                    value={kodeInline} onChange={e => setKodeInline(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); panggilTiketInline(kodeInline); } }}
                    placeholder="atau ketik kode tiket"
                    data-testid="nota-antrian-scan-input"
                    className="h-9 px-3 rounded-lg border border-slate-300 text-sm flex-1 min-w-[200px] bg-white"
                  />
                  <button type="button" onClick={() => panggilTiketInline(kodeInline)} disabled={busyPanggil}
                    data-testid="nota-antrian-scan-panggil"
                    className="h-9 px-4 rounded-lg bg-slate-900 text-white text-sm disabled:opacity-50">
                    {busyPanggil ? "Memanggil..." : "Panggil"}
                  </button>
                </div>
              </div>
            )}
            <div className="border-t border-slate-100 p-4 flex justify-end gap-2">
              <button onClick={notaGuard.requestClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">
                Batal
              </button>
              <button onClick={submit} disabled={saving || !!bundleAlasanTertahan} data-testid="btn-save-nota"
                title={bundleAlasanTertahan || undefined}
                className="h-10 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50 inline-flex items-center gap-1.5">
                <Send className="w-4 h-4" /> {saving ? "Menyimpan..." : (modalMode === "create" ? "Kirim ke Kasir" : "Simpan Perubahan")}
              </button>
            </div>
            {notaGuard.confirming && (
              <UnsavedGuardBar onStay={notaGuard.cancelClose} onDiscard={notaGuard.confirmDiscard} testid="nota-modal-guard-bar" />
            )}
          </div>
        </div>
      )}

      {/* Dialog konfirmasi PPN sebelum simpan (brief bos) — z-[60] supaya
          tampil DI ATAS modal input (z-50) yang masih terbuka di belakangnya. */}
      {ppnConfirm && (
        <div className="fixed inset-0 z-[60] bg-black/60 grid place-items-center p-4" onClick={() => setPpnConfirm(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="ppn-confirm-modal">
            <div className="bg-emerald-600 text-white px-5 py-3 rounded-t-2xl flex items-center justify-between">
              <h3 className="font-semibold text-sm">Konfirmasi PPN 11%</h3>
              <button onClick={() => setPpnConfirm(null)} className="p-1.5 hover:bg-white/20 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-slate-600">
                Nota ini akan dicatat KENA PPN. PPN akan dipisah dari pendapatan dan
                dicatat sebagai kewajiban pajak (PPN Keluaran) yang wajib disetor —
                pastikan sudah benar sebelum melanjutkan.
              </p>
              <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs space-y-0.5">
                <div className="flex justify-between text-slate-600">
                  <span>{diskonForm > 0 ? "DPP (sesudah diskon, sebelum PPN)" : "DPP (sebelum PPN)"}</span>
                  <span className="font-mono">{idr(dpp)}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>PPN ({PPN_PERSEN_PREVIEW}%)</span>
                  <span className="font-mono">{idr(ppnPreview)}</span>
                </div>
                <div className="flex justify-between font-semibold text-slate-900 pt-0.5 border-t border-slate-200">
                  <span>Total (DPP + PPN)</span>
                  <span className="font-mono">{idr(totalDenganPpn)}</span>
                </div>
              </div>
            </div>
            <div className="border-t border-slate-100 p-4 flex justify-end gap-2">
              <button onClick={() => setPpnConfirm(null)} data-testid="btn-ppn-kembali"
                className="h-10 px-4 rounded-lg border border-slate-200 text-sm">
                Kembali
              </button>
              <button onClick={() => doSave(ppnConfirm)} disabled={saving} data-testid="btn-ppn-lanjutkan"
                className="h-10 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                {saving ? "Menyimpan..." : "Lanjutkan"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Konfirmasi tiket untuk pengguna wajib-scan — ditanya, bukan dipilihkan
          diam-diam: FL bisa memegang tiket pelanggan A sementara nota yang
          sedang dibuat untuk pelanggan B. */}
      {konfirmasiTiket && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setKonfirmasiTiket(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="nota-antrian-konfirmasi">
            <div className="bg-emerald-600 text-white px-5 py-3 rounded-t-2xl flex items-center justify-between">
              <h3 className="font-semibold text-sm flex items-center gap-2"><Ticket className="w-4 h-4" /> Tiket antrian</h3>
              <button onClick={() => setKonfirmasiTiket(null)} data-testid="nota-antrian-konfirmasi-tutup"
                className="p-1.5 hover:bg-white/20 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              {konfirmasiTiket.pilihan.length === 1 ? (
                <p className="text-sm text-slate-800">
                  Nota ini untuk tiket <b>No. {konfirmasiTiket.pilihan[0].nomor_antrian}</b>?
                  <span className="block text-xs text-slate-500 mt-0.5 break-all">
                    {konfirmasiTiket.pilihan[0].kode_antrian} · {konfirmasiTiket.pilihan[0]._keterangan}
                  </span>
                </p>
              ) : (
                <>
                  <p className="text-sm text-slate-800">Nota ini untuk tiket yang mana?</p>
                  <ul className="space-y-1.5">
                    {konfirmasiTiket.pilihan.map(t => (
                      <li key={t.kode_antrian}>
                        <label className="flex items-start gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm cursor-pointer">
                          <input type="radio" name="nota-antrian-pilih"
                            checked={konfirmasiTiket.kode === t.kode_antrian}
                            onChange={() => setKonfirmasiTiket(k => ({ ...k, kode: t.kode_antrian }))}
                            data-testid={`nota-antrian-pilih-${t.kode_antrian}`}
                            className="mt-1" />
                          <span className="min-w-0">
                            <b>No. {t.nomor_antrian}</b>
                            <span className="block text-xs text-slate-500 break-all">{t.kode_antrian} · {t._keterangan}</span>
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            <div className="border-t border-slate-100 p-4 flex flex-wrap justify-end gap-2">
              <button onClick={() => { setKonfirmasiTiket(null); navigate("/antrian?tab=layani"); }}
                data-testid="nota-antrian-konfirmasi-bukan"
                className="h-10 px-4 rounded-lg border border-slate-200 text-sm">
                Bukan / scan tiket baru
              </button>
              <button onClick={pilihTiketKonfirmasi} disabled={!konfirmasiTiket.kode}
                data-testid="nota-antrian-konfirmasi-ya"
                className="h-10 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                Ya
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Panel chat AI — drawer fixed di atas modal (z-60 > z-50), unmount saat
          modal ditutup sehingga riwayat chat otomatis bersih untuk nota berikutnya */}
      {modal && aiPanel && bolehPakaiAI(user, can) && (
        <AIProductChat mode="sales" onApply={applyAiItems} onClose={() => setAiPanel(false)} />
      )}

      {/* Detail modal */}
      {detail && (
        <NotaDetail
          n={detail}
          user={user}
          canApprove={canApprove}
          onClose={() => setDetail(null)}
          onEdit={() => openEdit(detail)}
          onApprove={() => setKonfirmasiApprove(detail)}
          onReject={() => openReject(detail)}
          onDelete={() => deleteNota(detail)}
        />
      )}

      {/* Jendela approve — Total Pembayaran kasir (N3, D6). Dibuka oleh KETIGA
          tombol approve; `approve()` melempar galat supaya alert-nya tampil di
          jendela ini, bukan hanya sebagai toast yang cepat hilang. */}
      {konfirmasiApprove && (
        <KonfirmasiTotalPembayaran
          nota={konfirmasiApprove}
          onClose={() => setKonfirmasiApprove(null)}
          onKirim={(total) => approve(konfirmasiApprove, total)}
          // Draf lama yang ditahan server (bertempo + trade-in) — jalan keluarnya
          // dibuka dan disimpan dari form ini. Jendela ini hanya terbuka untuk
          // `canApprove`, dan `canEdit` draf pending = pemilik || canApprove,
          // jadi tombolnya tidak pernah berujung 403.
          onEdit={() => { const n = konfirmasiApprove; setKonfirmasiApprove(null); openEdit(n); }}
        />
      )}

      {/* Reject modal */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setRejectModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="reject-modal">
            <div className="bg-rose-600 text-white px-5 py-3 rounded-t-2xl flex items-center justify-between">
              <h3 className="font-semibold text-sm">Tolak Nota</h3>
              <button onClick={() => setRejectModal(null)} className="p-1.5 hover:bg-white/20 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5">
              <p className="text-xs text-slate-600 mb-3">
                Menolak nota dari <b>{rejectModal.nama_penjual}</b> untuk pelanggan <b>{rejectModal.nama_pelanggan}</b>.
              </p>
              <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                autoFocus rows={3} placeholder="Alasan penolakan..."
                data-testid="input-reject-reason"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            </div>
            <div className="border-t border-slate-100 p-4 flex justify-end gap-2">
              <button onClick={() => setRejectModal(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
              <button onClick={doReject} data-testid="btn-confirm-reject"
                className="h-10 px-5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium">
                Tolak Nota
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment methods manager modal */}
      {methodsModal && (
        <MetodePembayaranManager
          groups={paymentGroupsAll}
          onClose={() => setMethodsModal(false)}
          onChange={loadMethods}
        />
      )}

      {/* PIN PERSETUJUAN HAPUS (brief #23) */}
      {pinDialog && (
        <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => setPinDialog(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="pin-dialog">
            <div className="bg-rose-600 text-white px-5 py-3 rounded-t-2xl flex items-center justify-between">
              <h3 className="font-semibold text-sm inline-flex items-center gap-1.5">
                <Lock className="w-4 h-4" /> PIN Persetujuan Hapus
              </h3>
              <button onClick={() => setPinDialog(null)} className="p-1.5 hover:bg-white/20 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-slate-600">
                Penghapusan butuh persetujuan. Minta PIN ke pemegang izin (lihat Pusat Approval), lalu masukkan di sini.
              </p>
              <input value={pinDialog.pin}
                onChange={e => setPinDialog(d => ({ ...d, pin: e.target.value.replace(/\D/g, "").slice(0, 6), error: "" }))}
                maxLength={6} inputMode="numeric" placeholder="6 digit PIN" autoFocus
                data-testid="pin-input"
                className="w-full h-11 px-3 rounded-lg border border-slate-200 text-center text-lg tracking-[0.3em] font-mono" />
              {pinDialog.error && <p className="text-xs text-rose-600">{pinDialog.error}</p>}
            </div>
            <div className="border-t border-slate-100 p-4 flex justify-end gap-2">
              <button onClick={() => setPinDialog(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">
                Batal
              </button>
              <button onClick={submitPinDialog} disabled={pinDialog.submitting || pinDialog.pin.length !== 6}
                data-testid="pin-kirim"
                className="h-10 px-5 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-sm font-medium">
                {pinDialog.submitting ? "Memproses..." : "Kirim & Hapus"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== NotaDetail (rich preview) ====================
function NotaDetail({ n, user, canApprove, onClose, onEdit, onApprove, onReject, onDelete }) {
  const meta = STATUS_META[n.status] || STATUS_META.pending;
  const canEdit = n.status !== "approved" && (n.created_by_id === user?.id || canApprove);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl my-8 shadow-2xl" onClick={e => e.stopPropagation()}
        data-testid="nota-detail-modal">
        <div className={`bg-gradient-to-r from-${meta.color}-600 to-${meta.color}-500 text-white px-5 py-4 flex items-center justify-between rounded-t-2xl`}>
          <div>
            <p className="text-[11px] uppercase tracking-wider opacity-90">Detail Nota Sales</p>
            <h3 className="font-display text-lg font-semibold">{n.nama_pelanggan}</h3>
            <p className="text-[11px] opacity-90 mt-0.5 inline-flex items-center gap-1">
              <meta.icon className="w-3 h-3" /> {meta.label}
              {n.status === "approved" && n.approved_ref_id && (
                <span className="ml-2 px-1.5 py-0.5 rounded bg-white/20 text-[10px] font-mono">→ Sales #{n.approved_ref_id.slice(0, 8)}</span>
              )}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
          {/* Customer + sales grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
            <InfoRow icon={User} label="Nama Pelanggan" value={n.nama_pelanggan} />
            <InfoRow icon={Phone} label="No HP" value={n.no_hp || "-"} />
            <InfoRow icon={FileText} label="IMEI" value={n.imei || "-"} />
            <InfoRow icon={ShoppingCart} label="Sales" value={n.nama_penjual} />
            <InfoRow icon={MapPin} label="Cabang" value={n.branch || "-"} />
            <InfoRow icon={Clock} label="Tanggal · Waktu" value={`${n.tanggal} · ${n.waktu}`} />
          </div>

          {/* Items */}
          <div className="border-t border-slate-100 pt-3">
            <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
              <ShoppingCart className="w-3 h-3" /> Barang ({n.n_items})
            </p>
            <div className="space-y-1">
              {(n.items || []).map((it, i) => (
                <div key={i} className="flex justify-between text-xs bg-slate-50 rounded px-2 py-1.5">
                  <span>
                    {it.nama_barang}
                    <span className="ml-1 px-1 py-0 rounded bg-slate-200 text-slate-600 text-[9px] font-bold">{it.kode || "HP"}</span>
                    {it.is_bonus && (
                      <span className="ml-1 px-1 py-0 rounded bg-amber-100 text-amber-800 text-[9px] font-bold">Bonus</span>
                    )}
                    {diskonBaris(it) > 0 && (
                      <span className="block text-[10px] text-rose-700" data-testid={`nota-detail-diskon-${i}`}>
                        Diskon −{idr(diskonBaris(it))}
                      </span>
                    )}
                  </span>
                  <span className="font-mono font-semibold">{idr(it.harga)}</span>
                </div>
              ))}
            </div>
            <p className="text-right text-sm font-bold text-emerald-700 mt-2">Total Barang: {idr(n.total_harga)}</p>
            {/* N2: diskon nota (jumlah diskon barang, atau diskon nota lama) &
                nilai sesudahnya — medan turunan server, bukan hitungan layar. */}
            {Number(n._diskon_nota) > 0 && (
              <p className="text-right text-xs text-slate-600" data-testid="nota-detail-diskon-total">
                Diskon <span className="font-mono text-rose-700">−{idr(n._diskon_nota)}</span>
                {n.diskon_pic_approval ? ` (PIC ${n.diskon_pic_approval})` : ""}
                {" "}· Setelah diskon <span className="font-mono font-semibold text-slate-900">{idr(n._total_setelah_diskon)}</span>
              </p>
            )}
            {n.kena_ppn && (
              <div className="mt-1.5 flex justify-between text-xs bg-emerald-50 border border-emerald-100 rounded px-2 py-1.5"
                data-testid="nota-detail-ppn">
                <span className="text-emerald-800 font-medium">
                  Kena PPN {PPN_PERSEN_PREVIEW}% — angka final dihitung saat approve
                </span>
                <span className="font-mono font-semibold text-emerald-800">
                  ~{idr(hitungPpn(dppNota(n.total_harga, n._diskon_nota), PPN_PERSEN_PREVIEW))}
                </span>
              </div>
            )}
          </div>

          {/* Blok pembayaran (N3, D4/D5/D7): metode + keterangan, trade-in,
              DP, sisa tempo / kembalian downgrade — turunan server
              (`_baris_pembayaran`), bukan susunan layar sendiri. */}
          <div className="border-t border-slate-100 pt-3" data-testid="nota-detail-pembayaran">
            <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
              <CreditCard className="w-3 h-3" /> Pembayaran
            </p>
            <DaftarBarisPembayaran baris={n._baris_pembayaran?.baris} testidPrefix="nota-detail-bayar"
              kosong={(
                <p className="text-xs bg-slate-50 rounded px-2 py-1.5">
                  <b>{n.metode_pembayaran || "Cash"}</b>
                  <span className="text-slate-500 ml-2">(tidak ada pembayaran di kasir)</span>
                </p>
              )} />
            {!n.bayar_per_baris && Number(n.harga_terima) > 0 && (
              <p className="text-xs text-slate-500 mt-2">Harga Terima (nota lama): <span className="font-mono font-semibold text-slate-900">{idr(n.harga_terima)}</span></p>
            )}
          </div>

          {/* Trade-in */}
          {n.trade_in && (
            <div className="border-t border-slate-100 pt-3">
              <p className="text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1">
                <Repeat className="w-3 h-3" /> Trade-in
              </p>
              {n.trade_in_items && n.trade_in_items.length > 0 ? (
                <div className="space-y-1">
                  {n.trade_in_items.map((t, i) => (
                    <div key={i} className="flex justify-between items-start gap-2 text-xs bg-violet-50 border border-violet-200 rounded px-2 py-1.5">
                      <span className="text-violet-900 font-medium">
                        {t.nama || <span className="italic text-slate-400">tanpa nama</span>}
                        {t.imei && (
                          <span className="block font-mono text-[10px] text-violet-600 mt-0.5"
                            data-testid={`nota-detail-tradein-imei-${i}`}>IMEI {t.imei}</span>
                        )}
                      </span>
                      <span className="font-mono font-semibold text-violet-900 shrink-0">{idr(t.harga)}</span>
                    </div>
                  ))}
                  <p className="text-right text-xs text-slate-500 pt-1">
                    Total trade-in: <span className="font-mono font-semibold text-slate-900">
                      {idr(n.trade_in_items.reduce((s, t) => s + (parseFloat(t.harga) || 0), 0))}
                    </span>
                  </p>
                </div>
              ) : (
                <p className="text-xs bg-violet-50 border border-violet-200 rounded p-2">
                  <b>Ya</b> — {n.trade_in_note || <span className="italic text-slate-500">tanpa catatan</span>}
                </p>
              )}
            </div>
          )}

          {/* Notes */}
          {n.catatan && (
            <div className="border-t border-slate-100 pt-3">
              <p className="text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1">
                <StickyNote className="w-3 h-3" /> Catatan
              </p>
              <p className="text-xs bg-amber-50 border border-amber-200 rounded p-2">{n.catatan}</p>
            </div>
          )}

          {/* Reject reason */}
          {n.status === "rejected" && n.reject_reason && (
            <div className="text-xs bg-rose-50 border border-rose-200 rounded p-2">
              <b className="inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Alasan penolakan:</b> {n.reject_reason}
              <p className="text-[10px] text-rose-600 mt-1">Oleh {n.rejected_by} · {n.rejected_at?.slice(0, 16)?.replace("T", " ")}</p>
            </div>
          )}

          {/* Meta */}
          <p className="text-[10px] text-slate-400 pt-2 border-t border-slate-100">
            Dibuat oleh <b>{n.created_by_name || n.created_by_email}</b> · {n.created_at?.slice(0, 16)?.replace("T", " ")}
            {n.last_edited_at && <span> · edited {n.edit_count}x last {n.last_edited_at?.slice(0, 16)?.replace("T", " ")}</span>}
          </p>
        </div>
        <div className="border-t border-slate-100 p-4 flex flex-wrap justify-end gap-2">
          {canEdit && (
            <button onClick={onEdit} data-testid="btn-detail-edit"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-medium">
              <Pencil className="w-3.5 h-3.5" /> Edit
            </button>
          )}
          {canApprove && n.status === "pending" && (
            <>
              <button onClick={onApprove} data-testid="btn-detail-approve"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium">
                <CheckCircle2 className="w-3.5 h-3.5" /> Setujui & Transfer
              </button>
              <button onClick={onReject} data-testid="btn-detail-reject"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-medium">
                <XCircle className="w-3.5 h-3.5" /> Tolak
              </button>
            </>
          )}
          {(n.status !== "approved") && (n.created_by_id === user?.id || user?.role === "admin" || user?.role === "superadmin") && (
            <button onClick={onDelete}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium">
              <Trash2 className="w-3.5 h-3.5" /> Hapus
            </button>
          )}
          <button onClick={onClose} className="h-9 px-3 rounded-lg border border-slate-200 text-xs">Tutup</button>
        </div>
      </div>
    </div>
  );
}

// ==================== Autocomplete nama barang -> master produk ====================
// Opsional, gerbang izin inventory_produk. Barang di luar katalog (jasa,
// titipan, barang lama) TETAP bisa diketik bebas — SKU tidak pernah
// dipaksa. Kalau lookup gagal atau 403, otomatis diam-diam nonaktif tanpa
// toast merah yang menghalangi sales input nota.
function ItemNamaField({ index, item, bolehLookup, onPatch, resepBundlingMap }) {
  const [saran, setSaran] = useState([]);
  const debounceRef = useRef(null);
  const reqSeq = useRef(0);

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const cari = (teks) => {
    // Kategori di sini murni SARAN cadangan (baris masih ketik bebas, belum
    // tertaut SKU) — begitu `item.sku` sudah terisi (lihat `pilih()` di bawah),
    // baris kategori berubah jadi badge terkunci di JSX dan tebakan ini tidak
    // lagi relevan sampai sales melepas tautan SKU-nya sendiri.
    onPatch({ nama_barang: teks, kode: deriveKode(teks), kategori: deriveKategoriTeksBebas(teks) });
    if (!bolehLookup) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = teks.trim();
    if (q.length < 2) { setSaran([]); return; }
    debounceRef.current = setTimeout(async () => {
      const seq = ++reqSeq.current;
      try {
        const { data } = await api.get("/inventory/products/lookup", { params: { q } });
        if (seq === reqSeq.current) setSaran(data.items || []);
      } catch {
        // Diam-diam nonaktif — modul lookup mungkin tidak diizinkan atau error jaringan.
        if (seq === reqSeq.current) setSaran([]);
      }
    }, 300);
  };

  const pilih = (p) => {
    // Paket/bundling (poin 8, brief bos 23 Agu 2026) — sama alasan dengan
    // ItemNamaField.pilih() di Sales.jsx: scaffold komponen dibangun/direset
    // di SETIAP pemilihan, bukan hanya kalau produknya paket.
    const def = resepBundlingMap?.[(p.sku || "").trim().toUpperCase()];
    onPatch({
      nama_barang: p.nama,
      sku: p.sku,
      harga: p.harga_jual || 0,
      kode: deriveKode(p.nama),
      // Kategori diambil APA ADANYA dari master produk — TIDAK ditebak dari
      // nama. `kategori` wajib diisi saat produk dibuat (products.py), jadi
      // `p.kategori` semestinya selalu ada; "" hanya jaring pengaman kalau
      // data lama entah bagaimana kosong (dropdown akan kosong, memaksa
      // sales memilih manual, alih-alih diam-diam menebak salah).
      kategori: p.kategori || "",
      imei: "",
      komponen: def ? bangunKomponenKosong(def) : null,
    });
    setSaran([]);
  };

  return (
    <div className="relative">
      <input value={item.nama_barang} onChange={e => cari(e.target.value)}
        placeholder="Nama barang" data-testid={`item-cari-${index}`}
        className="h-9 px-2 rounded border border-slate-200 text-xs w-full" />
      {item.sku && (
        <span data-testid={`item-sku-badge-${index}`}
          className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5">
          SKU {item.sku}
          {/* Melepas SKU ikut melepas IMEI + rincian komponen paket yang sudah
              dipilih — keduanya cuma sah selama masih tertaut katalog
              (pemilihnya hilang begitu sku dikosongkan), jadi menyisakannya
              jadi nilai basi yang tidak bisa diedit lagi lewat UI. */}
          <button type="button" onClick={() => onPatch({ sku: "", imei: "", komponen: null })}
            title="Lepas tautan katalog — kembali ke ketik bebas"
            className="text-emerald-600 hover:text-emerald-900 leading-none">×</button>
        </span>
      )}
      {saran.length > 0 && (
        <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
          {/* Stok habis TIDAK bisa dipilih (keputusan bos). Ditegakkan juga di
              backend supaya tidak bisa ditembus; di sini sekadar mencegah
              kasir terlanjur memilih lalu ditolak saat menyimpan. Barang di
              luar katalog tetap bisa diketik bebas — itu jalan keluarnya.

              Paket (poin 8-11): SKU paket TIDAK PERNAH punya stok fisik sendiri
              (`p.stock_qty` selalu 0) — pakai STOK VIRTUAL (MIN komponen) untuk
              SKU yang cocok resep aktif, sama pola dengan Sales.jsx. */}
          {saran.map((p, n) => {
            const defPaket = resepBundlingMap?.[(p.sku || "").trim().toUpperCase()];
            const stokEfektif = defPaket ? (defPaket.stok_virtual?.qty ?? 0) : (p.stock_qty || 0);
            const habis = !(stokEfektif > 0);
            return (
              <button key={p.sku} type="button" disabled={habis}
                onClick={() => { if (!habis) pilih(p); }}
                title={habis ? "Stok habis — terima barangnya dulu lewat Pembelian & Terima" : ""}
                data-testid={`item-saran-${index}-${n}`}
                className={`w-full text-left px-3 py-2 text-xs border-b border-slate-50 last:border-0 ${
                  habis ? "bg-slate-50 opacity-60 cursor-not-allowed" : "hover:bg-emerald-50"}`}>
                <span className="font-medium text-slate-900">{p.nama}</span>
                <span className="font-mono text-[10px] text-slate-400 ml-1.5">{p.sku}</span>
                <span className="text-[10px] text-slate-500 ml-1.5">{idr(p.harga_jual || 0)}</span>
                {defPaket && (
                  <span className="text-[10px] ml-1.5 font-medium text-emerald-700">PAKET</span>
                )}
                <span className={`text-[10px] ml-1.5 font-medium ${habis ? "text-rose-600" : "text-emerald-600"}`}>
                  {habis ? "stok habis — tidak bisa dijual" : `stok ${stokEfektif}`}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ==================== Pemilih IMEI per baris -> unit inventory ====================
// Brief bos poin 4: IMEI HANYA boleh DIPILIH dari unit yang terdaftar &
// berstatus tersedia di inventory — tidak lagi diketik bebas. Beda dari
// ItemNamaField: mengetik di sini TIDAK PERNAH langsung mengisi `item.imei`
// (itu yang membuatnya "ketik bebas"), field baru terisi begitu kasir
// mengklik salah satu SARAN hasil pencarian. Sekali terisi, tampil sebagai
// badge (mirip badge SKU) — untuk mengubahnya, lepas dulu lewat "×".
//
// CATATAN BACKEND (baca sebelum mengandalkan ini sebagai satu-satunya
// pagar): validasi `_tolak_stok_kosong` (routers/sales.py, TIDAK disentuh
// dari sisi frontend ini) menolak IMEI yang tidak terdaftar/tidak
// "tersedia" per BARIS, tapi TIDAK mengunci/mereservasi unit — dua nota
// terpisah yang kebetulan memilih IMEI yang sama di baris yang berbeda
// keduanya akan lolos validasi server (unit tidak pernah ditandai "terjual"
// oleh sales.py, lihat routers/retur.py "BATASAN YANG SENGAJA DIPUTUSKAN").
// Yang bisa dicegah dari sini HANYA duplikat DALAM SATU nota yang sedang
// diisi (lewat `imeiTerpakaiLain`) — bukan lintas nota/transaksi.
function ItemImeiField({ index, item, bolehLookup, imeiTerpakaiLain, onPatch }) {
  // REKOMENDASI OTOMATIS (brief owner 29 Agu, Input Nota poin 1): begitu
  // produknya dipilih, unit yang tersedia untuk SKU itu langsung ditawarkan —
  // kasir tidak perlu mengetik apa pun. Sebelumnya daftar baru muncul setelah
  // 3 huruf diketik, dan kasir yang tidak tahu IMEI-nya tidak akan pernah
  // melihat pilihan apa pun.
  //
  // Aman terhadap "menguras gudang": permintaannya SELALU membawa `sku`, dan
  // backend hanya membolehkan panggilan tanpa `q` kalau `sku` disebut.
  useEffect(() => {
    if (!bolehLookup || !item.sku || item.imei) { setSaran([]); return; }
    let batal = false;
    (async () => {
      setMencari(true);
      try {
        const { data } = await api.get("/inventory/units/pilihan-jual",
                                       { params: { sku: item.sku } });
        if (!batal) terimaPilihanJual(data, setSaran, setInfoHarga, item.sku);
      } catch {
        if (!batal) setSaran([]);
      } finally {
        if (!batal) setMencari(false);
      }
    })();
    return () => { batal = true; };
  }, [bolehLookup, item.sku, item.imei]);
  const [cari, setCari] = useState("");
  const [saran, setSaran] = useState([]);
  const [mencari, setMencari] = useState(false);
  // Harga jual per IMEI (G2): SATU penerima respons (`terimaPilihanJual`) untuk
  // kedua jalur muat (rekomendasi per SKU & pencarian ketik) — bendera
  // `harga_unit_dipakai` yang disimpan di satu jalur saja membuat jalur lain
  // tidak pernah mengisi harga. Info harga MENGGABUNG respons per SKU: respons
  // cari hanya memuat unit yang cocok, harga unit yang baru dilepas tetap dikenali.
  const [infoHarga, setInfoHarga] = useState(null);
  const debounceRef = useRef(null);
  const reqSeq = useRef(0);

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const cariUnit = (teks) => {
    setCari(teks);
    if (!bolehLookup) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = teks.trim();
    if (q.length < 3) { setSaran([]); setMencari(false); return; }
    debounceRef.current = setTimeout(async () => {
      const seq = ++reqSeq.current;
      setMencari(true);
      try {
        const { data } = await api.get("/inventory/units/pilihan-jual", { params: { q, sku: item.sku } });
        if (seq === reqSeq.current) terimaPilihanJual(data, setSaran, setInfoHarga, item.sku);
      } catch {
        // Diam-diam nonaktif — modul lookup mungkin tidak diizinkan atau error jaringan.
        if (seq === reqSeq.current) setSaran([]);
      } finally {
        if (seq === reqSeq.current) setMencari(false);
      }
    }, 300);
  };

  const pilih = (u) => {
    // Harga per IMEI (G2): diisi hanya kalau harga baris masih "otomatis";
    // harga yang diketik kasir tidak ditimpa. Input Nota hanya menyunting DRAF
    // (nota tercatat tidak bisa diedit di sini), jadi tanpa `modeEdit`.
    const h = hargaSaatPilihUnit({ item, unit: u, info: infoHarga });
    onPatch({ imei: u.identifier, ...h.patch });
    if (h.pesan) toast.info(h.pesan);
    setCari("");
    setSaran([]);
  };

  if (item.imei) {
    return (
      <div className="mt-1">
        <span data-testid={`item-imei-badge-${index}`}
          className="inline-flex items-center gap-1 text-[10px] font-mono bg-violet-50 text-violet-700 border border-violet-200 rounded px-1.5 py-0.5">
          IMEI {item.imei}
          <button type="button" onClick={() => onPatch({ imei: "" })}
            title="Lepas pilihan IMEI"
            className="text-violet-600 hover:text-violet-900 leading-none">×</button>
        </span>
      </div>
    );
  }

  return (
    <div className="relative mt-1">
      <input value={cari} onChange={e => cariUnit(e.target.value)}
        placeholder={saran.length > 0 ? `Pilih 1 dari ${saran.length} IMEI tersedia, atau ketik untuk menyaring…`
                                      : "Cari IMEI/SN dari stok…"}
        data-testid={`item-imei-cari-${index}`}
        className="h-8 px-2 rounded border border-slate-200 text-[11px] w-full font-mono" />
      {saran.length > 0 && (
        <div data-testid={`item-imei-rekomendasi-${index}`}
             className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
          {saran.map((u, n) => {
            const dipakai = imeiTerpakaiLain?.has(u.identifier);
            const info = ringkasInfoUnit(u);
            return (
              <button key={u.id} type="button" disabled={dipakai}
                onClick={() => { if (!dipakai) pilih(u); }}
                title={dipakai ? "Unit ini sudah dipilih di baris lain pada nota ini" : ""}
                data-testid={`item-imei-saran-${index}-${n}`}
                className={`w-full text-left px-2 py-1.5 text-[11px] border-b border-slate-50 last:border-0 ${
                  dipakai ? "bg-slate-50 opacity-60 cursor-not-allowed" : "hover:bg-violet-50"}`}>
                <span className="font-mono font-medium text-slate-900">{u.identifier}</span>
                {/* INV-SN: SN hanya tampilan — yang ditulis ke nota tetap IMEI (identifier). */}
                {u.serial_number && <span className="font-mono text-slate-500 ml-1.5">SN {u.serial_number}</span>}
                {u.warehouse_nama && <span className="text-slate-400 ml-1.5">· {u.warehouse_nama}</span>}
                {/* G2: BH/garansi/harga per IMEI — membedakan HP second sejenis. */}
                {info && <span data-testid={`item-imei-info-${index}-${n}`} className="block text-[10px] text-slate-500">{info}</span>}
                {dipakai && <span className="text-rose-600 ml-1.5">sudah dipilih di baris lain</span>}
              </button>
            );
          })}
        </div>
      )}
      {!mencari && saran.length === 0 && (
        <p className="text-[10px] text-slate-400 mt-0.5" data-testid={`item-imei-kosong-${index}`}>
          {cari.trim().length >= 3
            ? "Tidak ada unit tersedia yang cocok."
            : "Belum ada unit ber-IMEI yang tersedia untuk produk ini."}
        </p>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-600">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-1.5">
      <Icon className="w-3.5 h-3.5 mt-0.5 text-slate-400 shrink-0" />
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
        <p className="text-slate-900 font-medium truncate">{value}</p>
      </div>
    </div>
  );
}

function StatCard({ I, label, value, tint }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className={`w-8 h-8 rounded-lg grid place-items-center bg-${tint}-50 text-${tint}-600 mb-2`}>
        <I className="w-4 h-4" />
      </div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="font-display text-2xl font-bold text-slate-900 mt-0.5">{value}</p>
    </div>
  );
}
